import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Table,
  TablePagination,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  FilterAltOff,
  CheckCircle,
  Cancel,
  Phone,
  Email,
  Language,
  WhatsApp,
  LocationOn,
  AccessTime,
  PendingOutlined,
  AddPhotoAlternate,
  Close,
  Visibility,
  NearMe,
  Chat,
  CalendarMonth,
  KeyboardArrowLeft,
  Map,
  Translate,
  Apps,
  Description,
  Gavel,
  ConfirmationNumber,
  Campaign,
  InfoOutlined,
  Smartphone,
  PublishedWithChanges,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';


import { contactsAPI, citiesAPI, categoriesAPI, districtsAPI, serviceSubCategoriesAPI, languageSupportAPI } from '../../services/api';
import { ApprovalStatus, ApprovalStatusLabels, APPROVAL_STATUS_FIELD } from '../../utils/enums';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageContacts, canDeleteContacts } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';

const defaultContactImage = 'https://placehold.co/400x300/7c3aed/ffffff?text=No+Image';

/** Form field name -> .NET model property name for multipart Contact create/update */
const CONTACT_FORM_TO_API = {
  title: 'Title',
  description: 'Description',
  infoNote: 'InfoNote',
  contactType: 'ContactType',
  cityId: 'CityId',
  categoryId: 'CategoryId',
  contactPerson: 'ContactPerson',
  address: 'Address',
  houseNumber: 'HouseNumber',
  postalCode: 'PostalCode',
  districtId: 'DistrictId',
  openingHours: 'OpeningHours',
  email: 'Email',
  phone: 'Phone',
  website: 'Website',
  whatsapp: 'Whatsapp',
  whatsappText: 'WhatsappText',
  longitude: 'Longitude',
  latitude: 'Latitude',
  approvalStatus: 'ApprovalStatus',
  approvalNote: 'ApprovalNote',
  isActive: 'IsActive',
  supportLanguages: 'SupportLanguages',
  clientTypes: 'ClientTypes',
  subCategoryIds: 'SubCategoryIds',
  ownerName: 'OwnerName',
  callToAction: 'CallToAction',
  isGoogleRatingAvailable: 'IsGoogleRatingAvailable',
  picturesRemove: 'PicturesRemove',
  documentsRequired: 'DocumentsRequired',
  rulesRegulations: 'RulesAndRegulations',
  pricingInformation: 'PricingInformation',
};

const CONTACT_ERROR_RULES = [
  { field: 'openingHours', test: (low) => low.includes('opening') && low.includes('hour') },
  { field: 'infoNote', test: (low) => low.includes('info note') },
  {
    field: 'subCategoryIds',
    test: (low) => low.includes('subcategory') || (low.includes('sub') && low.includes('categor')),
  },
  { field: 'postalCode', test: (low) => low.includes('postal') },
  { field: 'email', test: (low) => low.includes('email') },
  {
    field: 'whatsappText',
    test: (low) => low.includes('whatsapp') && (low.includes('text') || low.includes('message')),
  },
  { field: 'whatsapp', test: (low) => low.includes('whatsapp') },
  { field: 'phone', test: (low) => low.includes('phone') || low.includes('mobile') },
  { field: 'website', test: (low) => low.includes('website') || low.includes('url') },
  { field: 'title', test: (low) => low.includes('title') },
  { field: 'description', test: (low) => low.includes('description') },
  { field: 'address', test: (low) => low.includes('address') },
];

const parseContactApiErrors = (rawMessage) => {
  const out = {};
  if (!rawMessage) return out;
  const rest = [];
  String(rawMessage)
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((msg) => {
      const low = msg.toLowerCase();
      const rule = CONTACT_ERROR_RULES.find((entry) => entry.test(low));
      if (rule) out[rule.field] = msg;
      else rest.push(msg);
    });
  if (rest.length) out._form = rest.join('; ');
  return out;
};

function getIsActiveFilterParam(isActiveFilter) {
  if (isActiveFilter === 'true') return true;
  if (isActiveFilter === 'false') return false;
  return undefined;
}

function getFetchPage(isFilterChange, pageOverride, currentPage) {
  if (isFilterChange) return 0;
  if (pageOverride !== null) return pageOverride;
  return currentPage;
}

function getApprovalStatusChipColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.REJECTED) return 'error';
  if (status === ApprovalStatus.INACTIVE) return 'default';
  return 'warning';
}

function getApprovalDialogButtonColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.REJECTED) return 'error';
  return 'warning';
}


function getContactSubmitLabel(selectedContact, translate) {
  if (selectedContact) return translate('common.update');
  return translate('common.add');
}

function normalizeCommaSeparated(value) {
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return value.split(',').map((s) => s.replace(/‚/g, ',').trim()).filter(Boolean);
  }
  if (Array.isArray(value)) return value.map(s => typeof s === 'string' ? s.replace(/‚/g, ',') : s).filter(Boolean);
  return value || [];
}

function getContactListTotal(responseData, contactsListLength) {
  return responseData.totalRecord ?? responseData.totalCount ?? responseData.total ?? contactsListLength;
}

function parseSubCategoryIds(subCategoryIds) {
  if (!subCategoryIds) return [];
  if (typeof subCategoryIds === 'string') {
    return subCategoryIds.split(',').filter(Boolean);
  }
  return subCategoryIds;
}

function parsePricingInformation(raw) {
  if (!raw) return { headerTitle: '', headerDescription: '', plans: [] };
  let parsed = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return { headerTitle: '', headerDescription: '', plans: [] };
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch (e) {
        parsed = trimmed.split(',').map((s) => s.replace(/‚/g, ',').trim()).filter(Boolean);
      }
    } else {
      parsed = trimmed.split(',').map((s) => s.replace(/‚/g, ',').trim()).filter(Boolean);
    }
  }

  const headerDescription = !Array.isArray(parsed) && parsed && typeof parsed === 'object'
    ? String(parsed.headerDescription ?? parsed.HeaderDescription ?? '')
    : '';
  const headerTitle = !Array.isArray(parsed) && parsed && typeof parsed === 'object'
    ? String(parsed.headerTitle ?? parsed.HeaderTitle ?? '')
    : '';
  const rawPlans = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object'
      ? (Array.isArray(parsed.plans ?? parsed.Plans) ? (parsed.plans ?? parsed.Plans) : [parsed])
      : [];

  const plans = rawPlans.map((item) => {
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed.startsWith('{')) {
        try {
          const obj = JSON.parse(trimmed);
          if (obj && typeof obj === 'object') {
            return normalizePricingPlan(obj);
          }
        } catch (e) {}
      }
      return {
        title: item.replace(/‚/g, ','),
        badge: '',
        description: '',
        price: '',
        period: '',
        hint: '',
        highlights: [],
      };
    }
    if (item && typeof item === 'object') {
      return normalizePricingPlan(item);
    }
    return {
      title: '',
      badge: '',
      description: '',
      price: '',
      period: '',
      hint: '',
      highlights: [],
    };
  });
  return { headerTitle, headerDescription, plans };
}

function normalizePricingPlan(obj) {
  let highlights = [];
  const rawHighlights = obj.highlights ?? obj.Highlights;
  if (Array.isArray(rawHighlights)) {
    highlights = rawHighlights.map((h) => (typeof h === 'string' ? h.trim() : String(h || '').trim())).filter(Boolean);
  } else if (typeof rawHighlights === 'string' && rawHighlights.trim()) {
    try {
      const parsedH = JSON.parse(rawHighlights);
      if (Array.isArray(parsedH)) {
        highlights = parsedH.map((h) => String(h || '').trim()).filter(Boolean);
      } else {
        highlights = rawHighlights.split('\n').map((h) => h.trim()).filter(Boolean);
      }
    } catch (e) {
      highlights = rawHighlights.split('\n').map((h) => h.trim()).filter(Boolean);
    }
  }

  return {
    title: String(obj.title ?? obj.Title ?? ''),
    badge: String(obj.badge ?? obj.Badge ?? ''),
    description: String(obj.description ?? obj.Description ?? ''),
    price: String(obj.price ?? obj.Price ?? ''),
    period: String(obj.period ?? obj.Period ?? ''),
    hint: String(obj.hint ?? obj.Hint ?? ''),
    highlights,
  };
}

const CONTACT_FIELD_ORDER = [
  'cityId',
  'categoryId',
  'title',
  'description',
  'infoNote',
  'contactType',
  'contactPerson',
  'callToAction',
  'address',
  'districtId',
  'houseNumber',
  'postalCode',
  'openingHours',
  'email',
  'phone',
  'website',
  'whatsapp',
  'whatsappText',
  'supportLanguages',
  'clientTypes',
  'subCategoryIds',
  'documentsRequired',
  'rulesRegulations',
  'pricingInformation',
];

const validateUploadedFile = (file) => {
  if (!file) return null;
  const name = file.name || '';
  const lowercaseName = name.toLowerCase();

  // Allowed extensions
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg'];
  const hasAllowedExt = allowedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (!hasAllowedExt) {
    return 'Only .png, .jpg, .jpeg, and .svg files are allowed.';
  }

  // Block executables/installers explicitly
  const blockedExtensions = ['.exe', '.msi', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs', '.js'];
  const hasBlockedExt = blockedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (hasBlockedExt) {
    return 'Installer and executable files (.exe, .msi, .bat, etc.) are blocked.';
  }

  // Blocks multi-extensions (abc.xyz.png)
  const dots = name.split('.').length - 1;
  if (dots > 1) {
    return 'Files with multiple extensions (e.g. abc.xyz.png) are not allowed.';
  }

  // Max size: 5 MB
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must be under 5 MB.';
  }

  return null;
};

const isAddedByUser = (item) => {
  if (!item) return false;
  const type = String(
    item.addedByType ||
    item.AddedByType ||
    item.addedBy ||
    item.AddedBy ||
    ''
  ).toLowerCase().trim();
  return type === 'user' || type.includes('user');
};

const DAY_CONFIG = [
  { day: 'Monday', dayOfWeek: 1 },
  { day: 'Tuesday', dayOfWeek: 2 },
  { day: 'Wednesday', dayOfWeek: 3 },
  { day: 'Thursday', dayOfWeek: 4 },
  { day: 'Friday', dayOfWeek: 5 },
  { day: 'Saturday', dayOfWeek: 6 },
  { day: 'Sunday', dayOfWeek: 7 },
];

const formatTime12h = (time24) => {
  if (!time24) return '';
  const parts = String(time24).split(':');
  if (parts.length < 2) return time24;
  let h = parseInt(parts[0], 10);
  const m = parts[1].slice(0, 2);
  if (isNaN(h)) return time24;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  const hDisplay = h < 10 ? `0${h}` : `${h}`;
  return `${hDisplay}:${m} ${ampm}`;
};

const parseTimeTo24h = (timeStr) => {
  if (!timeStr) return '09:00';
  const str = String(timeStr).trim();
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const match = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (match) {
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = (match[3] || '').toUpperCase();
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    const hStr = h < 10 ? `0${h}` : `${h}`;
    return `${hStr}:${m}`;
  }
  return '09:00';
};

const formatSlot12h = (from24, to24) => {
  const f = formatTime12h(from24);
  const t = formatTime12h(to24);
  if (!f || !t) return '';
  return `${f} – ${t}`;
};

const formatDaySummary = (dayData) => {
  if (!dayData || !dayData.isOpen) return 'Closed';
  if (!dayData.slots || dayData.slots.length === 0) return 'Closed';
  return dayData.slots.map((s) => formatSlot12h(s.from, s.to)).filter(Boolean).join(', ');
};

const defaultBusinessHours = () =>
  DAY_CONFIG.map((cfg) => ({
    day: cfg.day,
    dayOfWeek: cfg.dayOfWeek,
    isOpen: cfg.day !== 'Saturday' && cfg.day !== 'Sunday',
    slots: [{ from: '09:00', to: '18:00' }]
  }));

const parseOpeningHoursString = (rawInput) => {
  if (!rawInput) return defaultBusinessHours();
  let parsed = rawInput;
  if (typeof rawInput === 'string') {
    const trimmed = rawInput.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(rawInput);
      } catch (e) {
        parsed = null;
      }
    }
  }

  if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.days)) {
      return DAY_CONFIG.map((cfg) => {
        const found = parsed.days.find((p) => p.day === cfg.day || p.dayOfWeek === cfg.dayOfWeek);
        if (found) {
          const isClosed = Boolean(found.isClosed);
          const sessions = Array.isArray(found.sessions) && found.sessions.length > 0
            ? found.sessions
            : [{ open: '09:00', close: '17:00' }];
          const slots = sessions.map((s) => ({
            from: parseTimeTo24h(s.open || s.from),
            to: parseTimeTo24h(s.close || s.to)
          }));
          return {
            day: cfg.day,
            dayOfWeek: cfg.dayOfWeek,
            isOpen: !isClosed,
            slots
          };
        }
        return { day: cfg.day, dayOfWeek: cfg.dayOfWeek, isOpen: false, slots: [{ from: '09:00', to: '18:00' }] };
      });
    }

    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].day) {
      return DAY_CONFIG.map((cfg) => {
        const found = parsed.find((p) => p.day === cfg.day);
        if (found) {
          return {
            day: cfg.day,
            dayOfWeek: cfg.dayOfWeek,
            isOpen: Boolean(found.isOpen),
            slots: Array.isArray(found.slots) && found.slots.length > 0 ? found.slots : [{ from: '09:00', to: '18:00' }]
          };
        }
        return { day: cfg.day, dayOfWeek: cfg.dayOfWeek, isOpen: false, slots: [{ from: '09:00', to: '18:00' }] };
      });
    }
  }

  let from = '09:00';
  let to = '18:00';
  if (typeof rawInput === 'string') {
    const times = rawInput.match(/\d{1,2}:\d{2}/g);
    if (times && times.length >= 2) {
      from = times[0].padStart(5, '0');
      to = times[1].padStart(5, '0');
    }
  }

  return DAY_CONFIG.map((cfg) => ({
    day: cfg.day,
    dayOfWeek: cfg.dayOfWeek,
    isOpen: cfg.day !== 'Saturday' && cfg.day !== 'Sunday',
    slots: [{ from, to }]
  }));
};

const serializeOpeningHours = (hoursList) => {
  const payload = {
    days: hoursList.map((d) => ({
      day: d.day,
      dayOfWeek: d.dayOfWeek,
      isClosed: !d.isOpen,
      sessions: !d.isOpen
        ? []
        : d.slots.map((s) => ({
            open: s.from || '09:00',
            close: s.to || '17:00'
          }))
    }))
  };
  return JSON.stringify(payload);
};

const BusinessHoursInput = ({ value, onChange, readOnly = false, error = false, helperText = '' }) => {
  const [hoursList, setHoursList] = useState(() => parseOpeningHoursString(value));

  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setHoursList(parseOpeningHoursString(value));
    }
  }, [value]);

  const updateHours = (newList) => {
    setHoursList(newList);
    if (onChange) {
      onChange(serializeOpeningHours(newList));
    }
  };

  const handleToggle = (index) => {
    if (readOnly) return;
    const newList = hoursList.map((item, i) =>
      i === index ? { ...item, isOpen: !item.isOpen } : item
    );
    updateHours(newList);
  };

  const handleTimeChange = (dayIndex, slotIndex, field, val) => {
    if (readOnly) return;
    const newList = hoursList.map((item, i) => {
      if (i !== dayIndex) return item;
      const newSlots = item.slots.map((slot, sIdx) =>
        sIdx === slotIndex ? { ...slot, [field]: val } : slot
      );
      return { ...item, slots: newSlots };
    });
    updateHours(newList);
  };

  const handleAddSlot = (dayIndex) => {
    if (readOnly) return;
    const newList = hoursList.map((item, i) => {
      if (i !== dayIndex) return item;
      if (item.slots.length >= 2) return item;
      return {
        ...item,
        slots: [...item.slots, { from: '09:00', to: '18:00' }]
      };
    });
    updateHours(newList);
  };

  const handleRemoveSlot = (dayIndex, slotIndex) => {
    if (readOnly) return;
    const newList = hoursList.map((item, i) => {
      if (i !== dayIndex) return item;
      const newSlots = item.slots.filter((_, sIdx) => sIdx !== slotIndex);
      return { ...item, slots: newSlots.length > 0 ? newSlots : [{ from: '09:00', to: '18:00' }] };
    });
    updateHours(newList);
  };

  return (
    <Box sx={{ width: '100%', mt: 0.5, mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AccessTime color="primary" sx={{ fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          Business Hours <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
        </Typography>
      </Box>

      <Box
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          p: 1.25,
          bgcolor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {hoursList.map((dayData, dayIndex) => {
          return (
            <Paper
              key={dayData.day}
              elevation={0}
              sx={{
                p: 1,
                px: 1.5,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                {/* Switch + Day Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130 }}>
                  <Switch
                    size="small"
                    color="primary"
                    checked={dayData.isOpen}
                    onChange={() => handleToggle(dayIndex)}
                    disabled={readOnly}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                    {dayData.day}
                  </Typography>
                </Box>

                {/* Slots or Closed */}
                {dayData.isOpen ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flex: 1 }}>
                    {dayData.slots.map((slot, slotIndex) => (
                      <Box
                        key={slotIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        <TextField
                          type="time"
                          size="small"
                          value={slot.from || '09:00'}
                          onChange={(e) => handleTimeChange(dayIndex, slotIndex, 'from', e.target.value)}
                          disabled={readOnly}
                          sx={{
                            width: 100,
                            bgcolor: '#ffffff',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: '#0f172a',
                              px: 0.5,
                              py: 0
                            },
                            '& input': { py: 0.5, px: 1 }
                          }}
                        />

                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>–</Typography>

                        <TextField
                          type="time"
                          size="small"
                          value={slot.to || '18:00'}
                          onChange={(e) => handleTimeChange(dayIndex, slotIndex, 'to', e.target.value)}
                          disabled={readOnly}
                          sx={{
                            width: 100,
                            bgcolor: '#ffffff',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: '#0f172a',
                              px: 0.5,
                              py: 0
                            },
                            '& input': { py: 0.5, px: 1 }
                          }}
                        />

                        <Box
                          sx={{
                            bgcolor: '#f1f5f9',
                            px: 1.5,
                            py: 0.4,
                            borderRadius: '6px',
                            minWidth: 140,
                            textAlign: 'center'
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: '0.775rem', color: '#0f172a' }}>
                            {formatSlot12h(slot.from, slot.to)}
                          </Typography>
                        </Box>

                        {!readOnly && dayData.slots.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSlot(dayIndex, slotIndex)}
                            sx={{ color: '#ef4444', p: 0.25 }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}

                    {!readOnly && dayData.slots.length < 2 && (
                      <Button
                        size="small"
                        startIcon={<Add sx={{ fontSize: 14 }} />}
                        onClick={() => handleAddSlot(dayIndex)}
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.775rem',
                          p: 0,
                          minWidth: 'auto',
                          lineHeight: 1.2,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                        }}
                      >
                        Add session
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      bgcolor: '#f1f5f9',
                      px: 2,
                      py: 0.4,
                      borderRadius: '6px'
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.775rem', color: '#64748b' }}>
                      Closed
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
      {error && helperText && (
        <FormHelperText error sx={{ mt: 1, ml: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

const Contacts = () => {
  /** Avoid duplicate cities + categories bootstrap when React Strict Mode runs effects twice in dev */
  const initialCitiesBootstrapRef = useRef(false);

  const { t } = useLanguage();

  const [languagesList, setLanguagesList] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimerRef = useRef(null);

  // Mobile preview state variables
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContact, setPreviewContact] = useState(null);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);

  const handleOpenPreview = async (contact) => {
    setIsLoading(true);
    let fullContact = contact;
    try {
      const res = await contactsAPI.getContact(contact.id);
      if (res?.data?.success && res.data.payload) {
        const payload = res.data.payload;
        fullContact = {
          ...contact,
          ...payload,
          logoPicture: payload.logoPicture || payload.LogoPicture || payload.profileImageUrl || payload.ProfileImageUrl || contact.logoPicture || contact.profileImageUrl || '',
          profileImageUrl: payload.logoPicture || payload.LogoPicture || payload.profileImageUrl || payload.ProfileImageUrl || contact.logoPicture || contact.profileImageUrl || '',
          cityName: cities.find((c) => c.id === (payload.cityId ?? contact.cityId))?.name || contact.cityName || 'Unknown',
          categoryName: categories.find((c) => c.id === (payload.categoryId ?? contact.categoryId))?.title_EN || contact.categoryName || 'Unknown',
          supportLanguages: normalizeCommaSeparated(payload.supportLanguages ?? contact.supportLanguages),
          clientTypes: normalizeCommaSeparated(payload.clientTypes ?? contact.clientTypes),
          subCategories: normalizeCommaSeparated(payload.subCategories ?? contact.subCategories),
          imageUrls: normalizeImageUrls(payload.imageUrls ?? payload.pictures ?? payload.bannerImage ?? contact.imageUrls),
          documentsRequired: (normalizeCommaSeparated(payload.documentsRequired ?? payload.DocumentsRequired) || []).filter(Boolean),
          rulesRegulations: (normalizeCommaSeparated(payload.rulesAndRegulations ?? payload.RulesAndRegulations ?? payload.rulesRegulations ?? payload.RulesRegulations) || []).filter(Boolean),
          pricingInformation: parsePricingInformation(payload.pricingInformation ?? payload.PricingInformation),
        };
      }
    } catch (err) {
      console.error("Error fetching contact detail for preview", err);
    } finally {
      setIsLoading(false);
    }
    setPreviewContact(fullContact);
    setActiveBannerIndex(0);
    setPreviewOpen(true);
  };

  const fetchLanguages = useCallback(async () => {
    try {
      const response = await languageSupportAPI.getLanguages(0, 500);
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        setLanguagesList(response.data.payload);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  }, []);

  const handleAddressSearch = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);

    addressSearchTimerRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(query)}&limit=5&in=countryCode:DEU&apiKey=6Y5UiHisZ3Ig9XwBJT20IwJMXar22qUlLWDpXYVKjw0`;
        const response = await fetch(url);
        const data = await response.json();
        setAddressSuggestions(data.items || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setAddressLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);
    };
  }, []);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const canAddEdit = canManageContacts(adminUser?.role);
  const canDelete = canDeleteContacts(adminUser?.role);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subCategoriesList, setSubCategoriesList] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [open, setOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [contactToApprove, setContactToApprove] = useState(null);
  const [filters, setFilters] = useState({
    title: '',
    cityId: '',
    categoryId: '',
    /** '' = all, 'true' / 'false' → GET ?isActive=… */
    isActive: '',
  });

  const dialogContentRef = useRef(null);

  const scrollToField = (fieldKey) => {
    setTimeout(() => {
      if (!dialogContentRef.current) return;
      const targetElement =
        dialogContentRef.current.querySelector(`[data-field="${fieldKey}"]`) ||
        dialogContentRef.current.querySelector(`[name="${fieldKey}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const inputEl = targetElement.querySelector('input, textarea, select, [tabindex="0"]') || targetElement;
        if (inputEl && typeof inputEl.focus === 'function') {
          try {
            inputEl.focus({ preventScroll: true });
          } catch (e) {}
        }
      }
    }, 100);
  };

  /** Debounced title for GET `title` query (avoids request per keystroke). */
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.title || '').trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.title]);

  useEffect(() => {
    setPage(0);
  }, [filters.isActive]);

  const normalizeImageUrls = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };


  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true) {
        const citiesList = response.data.payload || [];
        setCities(citiesList);

        if (citiesList.length > 0) {
          const firstCity = citiesList[0];
          const categoriesResponse = await categoriesAPI.getCategories(firstCity.id, 0, 100, { hasContact: true });
          if (categoriesResponse?.data?.success === true) {
            const categoriesList = categoriesResponse.data.payload || [];
            setCategories(categoriesList);

            if (categoriesList.length > 0) {
              const contactCategories = categoriesList.filter(c => c.hasContact === true || c.hasContact === 'true');
              if (contactCategories.length > 0) {
                const firstCategory =
                  contactCategories.find((c) => c.id === 1) || contactCategories[0];
                setFilters((prev) => ({
                  ...prev,
                  cityId: firstCity.id,
                  categoryId: firstCategory.id,
                }));
              } else {
                setFilters((prev) => ({
                  ...prev,
                  cityId: firstCity.id,
                  categoryId: '',
                }));
              }
            } else {
              setFilters((prev) => ({
                ...prev,
                cityId: firstCity.id,
                categoryId: '',
              }));
            }
          } else {
            setCategories([]);
            setFilters((prev) => ({
              ...prev,
              cityId: firstCity.id,
              categoryId: '',
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error in initial data load:', error);
      setCategories([]);
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await districtsAPI.getDistricts(cityId, 0, 1000);
      if (response?.data?.success === true) {
        const districtsList = response.data.payload || [];
        setDistricts(districtsList);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    if (!categoryId) {
      setSubCategoriesList([]);
      return;
    }
    try {
      const response = await serviceSubCategoriesAPI.getServiceSubCategories(categoryId, 0, 100);
      if (response?.data?.success === true) {
        setSubCategoriesList(response.data.payload || []);
      } else {
        setSubCategoriesList([]);
      }
    } catch (error) {
      console.error('Error fetching sub categories:', error);
      setSubCategoriesList([]);
    }
  };

  const fetchCategories = async (cityId, skipFilterUpdate = false) => {
    if (!cityId) {
      setCategories([]);
      if (!skipFilterUpdate) {
        setDistricts([]);
      }
      return;
    }
    try {
      const response = await categoriesAPI.getCategories(cityId, 0, 100, { hasContact: true });
      if (response?.data?.success === true) {
        const categoriesList = response.data.payload || [];
        setCategories(categoriesList);

        if (skipFilterUpdate) {
          fetchDistricts(cityId);
          return;
        }

        if (categoriesList.length > 0) {
          const contactCategories = categoriesList.filter(c => c.hasContact === true || c.hasContact === 'true');
          if (contactCategories.length > 0) {
            const defaultCategory =
              contactCategories.find((c) => c.id === 1) || contactCategories[0];
            setFilters((prev) => ({
              ...prev,
              cityId,
              categoryId: defaultCategory.id,
            }));
          } else {
            setFilters((prev) => ({
              ...prev,
              cityId,
              categoryId: '',
            }));
          }
        } else {
          setFilters((prev) => ({
            ...prev,
            cityId,
            categoryId: '',
          }));
        }

        fetchDistricts(cityId);
      } else {
        setCategories([]);
        setDistricts([]);
        if (!skipFilterUpdate) {
          setFilters((prev) => ({
            ...prev,
            cityId,
            categoryId: '',
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setDistricts([]);
      if (!skipFilterUpdate) {
        setFilters((prev) => ({
          ...prev,
          cityId,
          categoryId: '',
        }));
      }
    }
  };

  const fetchContacts = async (cityId = null, categoryId = null, pageOverride = null) => {
    setIsLoading(true);
    try {
      const effectiveCityId = cityId ?? filters.cityId;
      const effectiveCategoryId = categoryId ?? filters.categoryId;
      const isFilterChange = effectiveCityId !== filters.cityId || effectiveCategoryId !== filters.categoryId;

      if (isFilterChange) {
        setPage(0);
      }

      const pageToUse = getFetchPage(isFilterChange, pageOverride, page);

      const isActiveParam = getIsActiveFilterParam(filters.isActive);

      const response = await contactsAPI.getContacts(
        effectiveCityId || null,
        effectiveCategoryId || null,
        pageToUse,
        rowsPerPage,
        {
          title: debouncedTitle || undefined,
          isActive: isActiveParam,
        }
      );

      if (response?.data?.success === true) {
        const contactsList = response.data.payload || [];
        const total = getContactListTotal(response.data, contactsList.length);

        // Transform data to include city and category names and normalize data types
        const transformedContacts = contactsList.map((contact) => ({
          ...contact,
          ownerName: contact.ownerName || contact.OwnerName || '',
          approvedByName: contact.approvedByName || contact.ApprovedByName || contact.approvedBy || contact.ApprovedBy || '',
          addedByType: contact.addedByType || contact.AddedByType || contact.addedBy || contact.AddedBy || '',
          logoPicture: contact.logoPicture || contact.LogoPicture || contact.profileImageUrl || contact.ProfileImageUrl || '',
          profileImageUrl: contact.logoPicture || contact.LogoPicture || contact.profileImageUrl || contact.ProfileImageUrl || '',
          cityName: cities.find((c) => c.id === contact.cityId)?.name || 'Unknown',
          categoryName: categories.find((c) => c.id === contact.categoryId)?.title_EN || 'Unknown',
          supportLanguages: normalizeCommaSeparated(contact.supportLanguages),
          clientTypes: normalizeCommaSeparated(contact.clientTypes),
          subCategories: normalizeCommaSeparated(contact.subCategories),
          imageUrls: normalizeImageUrls(contact.imageUrls || contact.pictures || contact.bannerImage),
          documentsRequired: normalizeCommaSeparated(contact.documentsRequired || contact.DocumentsRequired) || [],
          rulesRegulations: normalizeCommaSeparated(contact.rulesAndRegulations || contact.RulesAndRegulations || contact.rulesRegulations || contact.RulesRegulations) || [],
          pricingInformation: parsePricingInformation(contact.pricingInformation || contact.PricingInformation),
        }));

        setContacts(transformedContacts);
        setTotalCount(total);
      } else {
        setContacts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  /** Refine current page if API ignores title/isActive. */
  const visibleContacts = useMemo(() => {
    const q = debouncedTitle.trim().toLowerCase();
    const hay = (v) => String(v ?? '').toLowerCase();
    return contacts.filter((c) => {
      if (q) {
        const match =
          hay(c.title).includes(q) ||
          hay(c.Title).includes(q) ||
          hay(c.description).includes(q) ||
          hay(c.Description).includes(q) ||
          hay(c.contactPerson).includes(q) ||
          hay(c.ownerName).includes(q) ||
          hay(c.approvedByName).includes(q) ||
          hay(c.addedByType).includes(q) ||
          hay(c.address).includes(q);
        if (!match) return false;
      }
      if (filters.isActive === 'true' && !(c.isActive === true || c.IsActive === true)) return false;
      if (filters.isActive === 'false' && !(c.isActive === false || c.IsActive === false)) return false;
      return true;
    });
  }, [contacts, debouncedTitle, filters.isActive]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    infoNote: '',
    contactType: '',
    cityId: filters.cityId || '',
    categoryId: filters.categoryId || '',
    contactPerson: '',
    address: '',
    houseNumber: '',
    postalCode: '',
    districtId: '',
    openingHours: serializeOpeningHours(defaultBusinessHours()),
    email: '',
    phone: '',
    website: '',
    whatsapp: '',
    whatsappText: '',
    longitude: '',
    latitude: '',
    approvalStatus: 'PENDING',
    approvalNote: '',
    Pictures: [], // New files
    existingBannerImages: [], // Existing URLs
    picturesRemove: [],
    profileImageUrlFile: null,
    existingProfileImageUrl: '',
    isActive: true,
    supportLanguages: '',
    clientTypes: '',
    subCategoryIds: [],
    ownerName: '',
    callToAction: '',
    isGoogleRatingAvailable: false,
    documentsRequired: [],
    rulesRegulations: [],
    pricingInformation: { headerTitle: '', headerDescription: '', plans: [] },
  });

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const [formErrors, setFormErrors] = useState({});

  const handleOpen = async (contact = null, isView = false) => {
    setFormErrors({});
    setIsViewMode(isView);
    if (contact) {
      setIsLoading(true);
      let fullContact = contact;
      try {
        const res = await contactsAPI.getContact(contact.id);
        if (res?.data?.success && res.data.payload) fullContact = res.data.payload;
      } catch (err) {
        console.error("Error fetching contact detail", err);
      }
      setIsLoading(false);
      setSelectedContact(fullContact);
      setFormData({
        title: fullContact.title || '',
        description: fullContact.description || '',
        infoNote: fullContact.infoNote || '',
        contactType: fullContact.contactType || fullContact.ContactType || '',
        cityId: fullContact.cityId || '',
        categoryId: fullContact.categoryId || '',
        contactPerson: fullContact.contactPerson || '',
        address: fullContact.address || '',
        houseNumber: fullContact.houseNumber || '',
        postalCode: fullContact.postalCode || '',
        districtId: fullContact.districtId || '',
        openingHours: serializeOpeningHours(parseOpeningHoursString(fullContact.openingHours)),
        email: fullContact.email || '',
        phone: fullContact.phone || '',
        website: fullContact.website || '',
        whatsapp: fullContact.whatsapp || '',
        whatsappText: fullContact.whatsappText || '',
        longitude: fullContact.longitude || '',
        latitude: fullContact.latitude || '',
        approvalStatus: fullContact.approvalStatus || 'PENDING',
        approvalNote: fullContact.approvalNote || '',
        Pictures: [],
        existingBannerImages: normalizeImageUrls(fullContact.imageUrls || fullContact.pictures || fullContact.bannerImage),
        picturesRemove: [],
        profileImageUrlFile: null,
        existingProfileImageUrl: fullContact.logoPicture || fullContact.LogoPicture || fullContact.profileImageUrl || fullContact.ProfileImageUrl || '',
        isActive: fullContact.isActive ?? true,
        supportLanguages: fullContact.supportLanguages || '',
        clientTypes: fullContact.clientTypes || '',
        subCategoryIds: parseSubCategoryIds(fullContact.subCategoryIds),
        ownerName: fullContact.ownerName || '',
        callToAction: fullContact.callToAction || '',
        isGoogleRatingAvailable: fullContact.isGoogleRatingAvailable || false,
        documentsRequired: (normalizeCommaSeparated(fullContact.documentsRequired || fullContact.DocumentsRequired) || []).filter(Boolean),
        rulesRegulations: (normalizeCommaSeparated(fullContact.rulesAndRegulations || fullContact.RulesAndRegulations || fullContact.rulesRegulations || fullContact.RulesRegulations) || []).filter(Boolean),
        pricingInformation: parsePricingInformation(fullContact.pricingInformation || fullContact.PricingInformation),
      });

      if (fullContact.cityId) {
        await fetchCategories(fullContact.cityId, true);
      }
      if (fullContact.categoryId) {
        fetchSubCategories(fullContact.categoryId);
      }
    } else {
      setSelectedContact(null);
      setFormData({
        title: '',
        description: '',
        infoNote: '',
        contactType: '',
        cityId: filters.cityId || '',
        categoryId: filters.categoryId || '',
        contactPerson: '',
        address: '',
        houseNumber: '',
        postalCode: '',
        districtId: '',
        openingHours: serializeOpeningHours(defaultBusinessHours()),
        email: '',
        phone: '',
        website: '',
        whatsapp: '',
        whatsappText: '',
        longitude: '',
        latitude: '',
        approvalStatus: 'PENDING',
        approvalNote: '',
        Pictures: [],
        existingBannerImages: [],
        picturesRemove: [],
        profileImageUrlFile: null,
        existingProfileImageUrl: '',
        isActive: true,
        supportLanguages: '',
        clientTypes: '',
        subCategoryIds: [],
        ownerName: '',
        callToAction: '',
        isGoogleRatingAvailable: false,
        documentsRequired: [],
        rulesRegulations: [],
        pricingInformation: { headerTitle: '', headerDescription: '', plans: [] },
      });

      if (filters.cityId) {
        await fetchDistricts(filters.cityId);
      }
      if (filters.categoryId) {
        fetchSubCategories(filters.categoryId);
      }
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedContact(null);
    setIsViewMode(false);
    setFormErrors({});
    setFormData({
      title: '',
      description: '',
      infoNote: '',
      contactType: '',
      cityId: filters.cityId || '',
      categoryId: filters.categoryId || '',
      contactPerson: '',
      address: '',
      houseNumber: '',
      postalCode: '',
      districtId: '',
      openingHours: serializeOpeningHours(defaultBusinessHours()),
      email: '',
      phone: '',
      website: '',
      whatsapp: '',
      whatsappText: '',
      longitude: '',
      latitude: '',
      approvalStatus: 'PENDING',
      approvalNote: '',
      Pictures: [],
      existingBannerImages: [],
      picturesRemove: [],
      profileImageUrlFile: null,
      existingProfileImageUrl: '',
      isActive: true,
      supportLanguages: '',
      clientTypes: '',
      subCategoryIds: [],
      ownerName: '',
      callToAction: '',
      isGoogleRatingAvailable: false,
      documentsRequired: [],
      rulesRegulations: [],
        pricingInformation: { headerTitle: '', headerDescription: '', plans: [] },
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let errorMsg = '';

    for (const file of files) {
      const error = validateUploadedFile(file);
      if (error) {
        errorMsg = error;
      } else {
        validFiles.push(file);
      }
    }

    if (errorMsg) {
      showToast(errorMsg, 'warning');
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        Pictures: [...prev.Pictures, ...validFiles],
      }));
    }
    e.target.value = '';
  };

  const handleRemoveImage = (index, isExisting = false) => {
    setFormData((prev) => {
      if (isExisting) {
        const removedUrl = prev.existingBannerImages[index];
        const updatedExisting = [...prev.existingBannerImages];
        updatedExisting.splice(index, 1);
        return {
          ...prev,
          existingBannerImages: updatedExisting,
          picturesRemove: [
            ...(prev.picturesRemove || []),
            removedUrl
          ].filter(Boolean)
        };
      } else {
        const updatedPictures = [...prev.Pictures];
        updatedPictures.splice(index, 1);
        return { ...prev, Pictures: updatedPictures };
      }
    });
  };

  const handleProfileImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateUploadedFile(file);
      if (error) {
        showToast(error, 'warning');
        e.target.value = '';
        return;
      }
      setFormData((prev) => ({
        ...prev,
        profileImageUrlFile: file
      }));
    }
  };

  const handleRemoveProfileImage = () => {
    setFormData((prev) => ({
      ...prev,
      profileImageUrlFile: null,
      existingProfileImageUrl: ''
    }));
  };

  const handleChange = (e) => {
    const { name } = e.target;
    const value = name === 'contactPerson'
      ? e.target.value.slice(0, 100)
      : typeof e.target.value === 'string' ? e.target.value.slice(0, 250) : e.target.value;
    if (name) {
      setFormErrors((prev) => ({ ...prev, [name]: '', _form: '' }));
    }
    if (name === 'categoryId') {
      setFormData((prev) => ({
        ...prev,
        categoryId: value,
        subCategoryIds: [],
      }));
      fetchSubCategories(value);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayFieldChange = (fieldName, index, newValue) => {
    const limitedValue = String(newValue || '').slice(0, 250);
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      updatedArray[index] = limitedValue;
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: '',
      _form: '',
    }));
  };

  const handleAddArrayItem = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ''],
    }));
  };

  const handleRemoveArrayItem = (fieldName, index) => {
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      updatedArray.splice(index, 1);
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
  };

  const renderArrayField = (label, fieldName) => {
    const items = formData[fieldName] || [];
    return (
      <Grid item xs={12} data-field={fieldName}>
        <Box sx={{ mt: 1, mb: 1, p: 2, border: '1px solid #e5e7eb', borderRadius: '12px', bgcolor: '#f9fafb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
              {label}
            </Typography>
            {!isViewMode && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => handleAddArrayItem(fieldName)}
                sx={{
                  color: '#0F3D3E',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.04)' }
                }}
              >
                {t('contacts.addItem') || 'Add Item'}
              </Button>
            )}
          </Box>

          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pl: 1 }}>
              {t('contacts.noItemsAdded') || 'No items added yet. Click "Add Item" to add.'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.map((item, index) => {
                const isOverLimit = (item || '').length > 200;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={4}
                      value={item}
                      onChange={(e) => handleArrayFieldChange(fieldName, index, e.target.value)}
                      placeholder={`Enter ${label.toLowerCase()} text or URL`}
                      error={isOverLimit}
                      InputProps={{ readOnly: isViewMode }}
                      helperText={
                        isOverLimit
                          ? `${t('contacts.charLimitExceeded') || 'Character limit exceeded:'} ${(item || '').length}/200`
                          : `${(item || '').length}/200`
                      }
                      FormHelperTextProps={{
                        sx: { color: isOverLimit ? 'error.main' : 'text.secondary', textAlign: 'right' }
                      }}
                    />
                    {!isViewMode && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveArrayItem(fieldName, index)}
                        sx={{ mt: 0.5 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const handleAddPricingPlan = () => {
    setFormData((prev) => ({
      ...prev,
      pricingInformation: {
        ...(prev.pricingInformation || {}),
        plans: [
        ...(prev.pricingInformation?.plans || []),
        {
          title: '',
          badge: '',
          description: '',
          price: '',
          period: '/ month',
          hint: '',
          highlights: [],
        },
        ],
      },
    }));
  };

  const handlePricingHeaderChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      pricingInformation: {
        ...(prev.pricingInformation || { plans: [] }),
        [field]: String(value || '').slice(0, field === 'headerDescription' ? 150 : 50),
      },
    }));
  };

  const handlePricingPlanChange = (index, field, value) => {
    const maxLengths = { title: 50, description: 500, price: 12, hint: 250, badge: 50 };
    const limitedValue = typeof value === 'string' && maxLengths[field]
      ? value.slice(0, maxLengths[field])
      : value;
    setFormData((prev) => {
      const pricingInformation = prev.pricingInformation || { headerTitle: '', headerDescription: '', plans: [] };
      const updated = [...(pricingInformation.plans || [])];
      updated[index] = {
        ...(updated[index] || {}),
        [field]: limitedValue,
      };
      return {
        ...prev,
        pricingInformation: { ...pricingInformation, plans: updated },
      };
    });
  };

  const handleRemovePricingPlan = (index) => {
    setFormData((prev) => {
      const pricingInformation = prev.pricingInformation || { headerTitle: '', headerDescription: '', plans: [] };
      const updated = [...(pricingInformation.plans || [])];
      updated.splice(index, 1);
      return {
        ...prev,
        pricingInformation: { ...pricingInformation, plans: updated },
      };
    });
  };

  const handleAddHighlight = (planIndex, text) => {
    if (!text || !text.trim()) return;
    setFormData((prev) => {
      const pricingInformation = prev.pricingInformation || { headerTitle: '', headerDescription: '', plans: [] };
      const updated = [...(pricingInformation.plans || [])];
      const plan = updated[planIndex] || {};
      const highlights = [...(plan.highlights || []), text.trim()];
      updated[planIndex] = { ...plan, highlights };
      return {
        ...prev,
        pricingInformation: { ...pricingInformation, plans: updated },
      };
    });
  };

  const handleEditHighlight = (planIndex, highlightIndex, text) => {
    setFormData((prev) => {
      const pricingInformation = prev.pricingInformation || { headerTitle: '', headerDescription: '', plans: [] };
      const updated = [...(pricingInformation.plans || [])];
      const plan = updated[planIndex] || {};
      const highlights = [...(plan.highlights || [])];
      highlights[highlightIndex] = text;
      updated[planIndex] = { ...plan, highlights };
      return {
        ...prev,
        pricingInformation: { ...pricingInformation, plans: updated },
      };
    });
  };

  const handleRemoveHighlight = (planIndex, highlightIndex) => {
    setFormData((prev) => {
      const pricingInformation = prev.pricingInformation || { headerTitle: '', headerDescription: '', plans: [] };
      const updated = [...(pricingInformation.plans || [])];
      const plan = updated[planIndex] || {};
      const highlights = [...(plan.highlights || [])];
      highlights.splice(highlightIndex, 1);
      updated[planIndex] = { ...plan, highlights };
      return {
        ...prev,
        pricingInformation: { ...pricingInformation, plans: updated },
      };
    });
  };

  const renderPricingInformationSection = () => {
    const plans = formData.pricingInformation?.plans || [];
    return (
      <Grid item xs={12} data-field="pricingInformation">
        <Box
          sx={{
            mt: 2,
            mb: 2,
            p: 2.5,
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            bgcolor: '#f8fafc',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConfirmationNumber sx={{ color: '#0F3D3E', fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                  {t('contacts.pricingInformation') || 'Pricing Information & Plans'}
                </Typography>
                <Chip
                  label={`${plans.length} ${plans.length === 1 ? 'Plan' : 'Plans'}`}
                  size="small"
                  sx={{
                    bgcolor: plans.length > 0 ? '#e6fffa' : '#f1f5f9',
                    color: plans.length > 0 ? '#047481' : '#64748b',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {t('contacts.pricingDescription') || 'Add and manage pricing packages, subscription tiers, or fees with highlights and badges.'}
              </Typography>
            </Box>

            {!isViewMode && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleAddPricingPlan}
                sx={{
                  bgcolor: '#0F3D3E',
                  color: '#ffffff',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 2,
                  boxShadow: '0 2px 4px rgba(15, 61, 62, 0.15)',
                  '&:hover': { bgcolor: '#0b2e2f' },
                }}
              >
                {t('contacts.addPricingPlan') || 'Add Pricing Plan'}
              </Button>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('contacts.pricingHeaderTitle') || 'Pricing Header Title'}
                value={formData.pricingInformation?.headerTitle || ''}
                onChange={(e) => handlePricingHeaderChange('headerTitle', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 50 }}
                error={Boolean(formErrors.pricingInformation?.headerTitle)}
                helperText={formErrors.pricingInformation?.headerTitle || undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label={t('contacts.pricingHeaderDescription') || 'Pricing Header Description'}
                value={formData.pricingInformation?.headerDescription || ''}
                onChange={(e) => handlePricingHeaderChange('headerDescription', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 150 }}
                error={Boolean(formErrors.pricingInformation?.headerDescription)}
                helperText={formErrors.pricingInformation?.headerDescription || undefined}
              />
            </Grid>
          </Grid>

          {plans.length === 0 ? (
            <Box
              sx={{
                p: 4,
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                bgcolor: '#ffffff',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: '#ecfdf5',
                  color: '#0F3D3E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ConfirmationNumber sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {t('contacts.noPricingPlans') || 'No pricing plans added yet'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', maxWidth: 450, mt: 0.5 }}>
                  Configure pricing options, badges like "Most Popular", prices, periods, hints, and bullet features.
                </Typography>
              </Box>
              {!isViewMode && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddPricingPlan}
                  sx={{
                    mt: 0.5,
                    borderColor: '#0F3D3E',
                    color: '#0F3D3E',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#0b2e2f', bgcolor: 'rgba(15, 61, 62, 0.04)' },
                  }}
                >
                  {t('contacts.addPricingPlan') || 'Add First Plan'}
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {plans.map((plan, index) => (
                <PricingPlanCard
                  key={index}
                  plan={plan}
                  index={index}
                  isViewMode={isViewMode}
                  onChange={handlePricingPlanChange}
                  onRemove={handleRemovePricingPlan}
                  onAddHighlight={handleAddHighlight}
                  onEditHighlight={handleEditHighlight}
                  onRemoveHighlight={handleRemoveHighlight}
                  t={t}
                  errors={formErrors.pricingInformation?.plans?.[index] || {}}
                />
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const openApprovalDialog = (contact, status) => {
    const rowActive = contact?.isActive === true || contact?.IsActive === true;
    if (!rowActive) return;
    setContactToApprove(contact);
    setApprovalStatus(status);
    setApprovalNote('');
    setApprovalDialogOpen(true);
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setContactToApprove(null);
    setApprovalStatus('');
    setApprovalNote('');
  };

  // Helper to update a contact in the local list (optimistic update)
  const updateContactInList = (contactId, updatedData) => {
    setContacts(prev => prev.map(contact => {
      if (contact.id !== contactId) return contact;
      const merged = { ...contact, ...updatedData };
      return {
        ...merged,
        cityName: cities.find(c => c.id === merged.cityId)?.name || merged.cityName || 'Unknown',
        categoryName: categories.find(c => c.id === merged.categoryId)?.title_EN || merged.categoryName || 'Unknown',
        supportLanguages: normalizeCommaSeparated(merged.supportLanguages),
        clientTypes: normalizeCommaSeparated(merged.clientTypes),
        subCategories: normalizeCommaSeparated(merged.subCategories),
        imageUrls: normalizeImageUrls(merged.imageUrls || merged.pictures || merged.bannerImage),
        documentsRequired: normalizeCommaSeparated(merged.documentsRequired || merged.DocumentsRequired) || [],
        rulesRegulations: normalizeCommaSeparated(merged.rulesAndRegulations || merged.RulesAndRegulations || merged.rulesRegulations || merged.RulesRegulations) || [],
        pricingInformation: parsePricingInformation(merged.pricingInformation || merged.PricingInformation),
      };
    }));
  };

  const arrayToString = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.join(',');
    return '';
  };

  const handleApproval = async () => {
    // Validate approval note for REJECTED status
    if (approvalStatus === ApprovalStatus.REJECTED && !approvalNote.trim()) {
      showToast(t('contacts.toastNoteReject'), 'warning');
      return;
    }

    try {
      setIsSubmitting(true);
      const c = contactToApprove;
      const payload = {
        id: c.id,
        approvalStatus,
        approvalNote: approvalNote.trim()
      };
      const response = await contactsAPI.updateContactApproval(payload);

      if (response?.data?.success === true) {
        const updatedContact = response?.data?.payload?.contact;
        if (updatedContact) {
          updateContactInList(c.id, updatedContact);
        } else {
          updateContactInList(c.id, {
            approvalStatus: approvalStatus,
            approvalNote: approvalNote.trim()
          });
        }
        closeApprovalDialog();
        showToast(t('contacts.toastUpdated'), 'success');
        fetchContacts(filters.cityId, filters.categoryId);
      } else {
        const errMsg = response?.data?.errorMessage || response?.data?.message || 'Unknown error';
        showToast(t('contacts.toastApprovalErr') + ': ' + errMsg, 'error');
      }
    } catch (error) {
      console.error('Error updating approval status:', error);
      const errMsg = error?.response?.data?.errorMessage || error?.response?.data?.message || error?.message || 'Unknown error';
      showToast(t('contacts.toastApprovalErr2') + ': ' + errMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initial load - cities + categories for default city (contacts loaded via effect below)
  useEffect(() => {
    if (initialCitiesBootstrapRef.current) return;
    initialCitiesBootstrapRef.current = true;
    fetchCities();
    fetchLanguages();
  }, [fetchLanguages]);

  // Single place to load contacts when filters or pagination change
  useEffect(() => {
    if (!filters.cityId) {
      setContacts([]);
      setTotalCount(0);
      return;
    }
    fetchContacts();
  }, [filters.cityId, filters.categoryId, filters.isActive, debouncedTitle, page, rowsPerPage]);

  // Reset categories when city filter is cleared
  useEffect(() => {
    if (!filters.cityId) {
      setCategories([]);
    }
  }, [filters.cityId]);

  const handleSubmit = async () => {
    const next = {};
    const pricingInfo = formData.pricingInformation || { plans: [] };
    const pricingPlans = Array.isArray(pricingInfo.plans) ? pricingInfo.plans : [];
    const pricingHeaderErrors = {
      headerTitle: (pricingInfo.headerTitle || '').length > 50 ? t('contacts.pricingHeaderTitleMax') : '',
      headerDescription: (pricingInfo.headerDescription || '').length > 150
        ? t('contacts.pricingHeaderDescriptionMax')
        : '',
    };
    const pricingValidationErrors = pricingPlans.map((plan) => {
      if (typeof plan === 'string') {
        return {
          title: plan.trim() ? '' : t('contacts.pricingTitleRequired'),
          description: t('contacts.pricingDescriptionRequired'),
        };
      }
      return {
        title: !plan?.title?.trim()
          ? t('contacts.pricingTitleRequired')
          : String(plan.title).length > 50 ? t('contacts.pricingTitleMax') : '',
        price: !plan?.price || /^(?:[€$£]\s*)?\d{1,6}(?:\.\d{1,2})?$/.test(String(plan.price).trim())
          ? ''
          : t('contacts.pricingPriceInvalid'),
        description: !plan?.description?.trim()
          ? t('contacts.pricingDescriptionRequired')
          : (plan?.description || '').length > 500 ? t('contacts.pricingDescriptionMax') : '',
        hint: (plan?.hint || '').length > 250 ? t('contacts.pricingHintMax') : '',
        highlights: (plan?.highlights || []).map((feature) =>
          (feature || '').length > 250 ? t('contacts.pricingFeatureMax') : ''
        ),
      };
    });
    const hasPlanValidationErrors = pricingValidationErrors.some((errors) => Object.values(errors).some((error) =>
      Array.isArray(error) ? error.some(Boolean) : Boolean(error)
    ));
    if (hasPlanValidationErrors || pricingHeaderErrors.headerTitle || pricingHeaderErrors.headerDescription) {
      next.pricingInformation = {
        ...pricingHeaderErrors,
        plans: pricingValidationErrors,
      };
    }
    if (!formData.title?.trim()) next.title = t('contacts.errFieldRequired');
    if (!formData.cityId) next.cityId = t('contacts.errFieldRequired');
    if (!formData.categoryId) next.categoryId = t('contacts.errFieldRequired');
    if (!formData.contactType) next.contactType = t('contacts.errFieldRequired');
    if (!formData.description?.trim()) next.description = t('contacts.errFieldRequired');
    if (!formData.address?.trim()) {
      next.address = t('contacts.errFieldRequired');
    } else if (!formData.latitude || !formData.longitude) {
      next.address = 'Please select address from auto-fetch suggestions';
    }
    if (!formData.infoNote?.trim()) next.infoNote = t('contacts.errInfoNoteRequired');
    const postal = formData.postalCode?.trim() || '';
    if (!postal) next.postalCode = t('contacts.errPostalRequired');
    else if (!/^\d{5}$/.test(postal)) next.postalCode = t('contacts.errPostalDigits5');
    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      next.email = t('contacts.errEmailInvalid');
    }
    if (!formData.subCategoryIds?.length) {
      next.subCategoryIds = t('contacts.errSubCategoryRequired');
    }
    if (!formData.districtId) {
      next.districtId = t('contacts.errFieldRequired');
    }
    if (!normalizeCommaSeparated(formData.supportLanguages)?.length) {
      next.supportLanguages = t('contacts.errFieldRequired');
    }
    const wa = (formData.whatsapp && String(formData.whatsapp).trim()) || '';
    if (wa.length > 20) next.whatsapp = t('contacts.errWhatsappMax20');

    // Character count validation (max 200) for array string fields
    const arrayFields = ['documentsRequired', 'rulesRegulations'];
    let hasArrayError = false;
    arrayFields.forEach((field) => {
      const arr = formData[field] || [];
      const hasInvalidItem = arr.some((item) => (item || '').length > 250);
      if (hasInvalidItem) {
        next[field] = 'One or more items exceed 250 characters';
        hasArrayError = true;
      }
    });

    if (Object.keys(next).length > 0) {
      setFormErrors(next);
      if (hasArrayError) {
        showToast("Please ensure all items in Documents Required and Terms & Conditions are within 250 characters.", 'warning');
      }
      const firstErrorField = CONTACT_FIELD_ORDER.find((field) => next[field]);
      if (firstErrorField) {
        scrollToField(firstErrorField);
      }
      return;
    }

    if (formData.latitude) {
      const lat = Number.parseFloat(formData.latitude);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        showToast("Latitude must be between -90 and 90", 'warning');
        return;
      }
    }

    if (formData.longitude) {
      const lng = Number.parseFloat(formData.longitude);
      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        showToast("Longitude must be between -180 and 180", 'warning');
        return;
      }
    }

    setFormErrors({});
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();

      Object.entries(CONTACT_FORM_TO_API).forEach(([formKey, apiKey]) => {
        const value = formData[formKey];
        if (value === undefined) return;
        if (formKey === 'openingHours') {
          const serialized = serializeOpeningHours(parseOpeningHoursString(value));
          formDataToSend.append(apiKey, serialized);
        } else if (formKey === 'supportLanguages') {
          const s = Array.isArray(value) ? value.join(',') : String(value || '').trim();
          formDataToSend.append(apiKey, s);
        } else if (formKey === 'pricingInformation') {
          const rawPlans = Array.isArray(value?.plans) ? value.plans : [];
          const cleanedPricing = rawPlans
            .filter((p) => p && (
              (typeof p === 'string' && p.trim()) ||
              (p.title && p.title.trim()) ||
              (p.price && p.price.trim()) ||
              (p.description && p.description.trim()) ||
              (p.hint && p.hint.trim()) ||
              (Array.isArray(p.highlights) && p.highlights.length > 0)
            ))
            .map((p) => {
              if (typeof p === 'string') {
                return {
                  title: p.trim(),
                  badge: '',
                  description: '',
                  price: '',
                  period: '',
                  hint: '',
                  highlights: []
                };
              }
              return {
                title: (p.title || '').trim(),
                badge: (p.badge || '').trim(),
                description: (p.description || '').trim(),
                price: (p.price || '').trim(),
                period: (p.period || '').trim(),
                hint: (p.hint || '').trim(),
                highlights: Array.isArray(p.highlights)
                  ? p.highlights.map((h) => (h || '').trim()).filter(Boolean)
                  : []
              };
            });
          const pricingPayload = {
            headerTitle: (value?.headerTitle || '').trim(),
            headerDescription: (value?.headerDescription || '').trim(),
            plans: cleanedPricing,
          };
          const hasPricingData = pricingPayload.headerTitle || pricingPayload.headerDescription || cleanedPricing.length > 0;
          formDataToSend.append(apiKey, hasPricingData ? JSON.stringify(pricingPayload) : '');
        } else if (Array.isArray(value)) {
          const filteredArray = (formKey === 'documentsRequired' || formKey === 'rulesRegulations')
            ? value.map(item => (item || '').replace(/,/g, '‚').trim()).filter(Boolean)
            : value;
          if (filteredArray.length === 0) {
            formDataToSend.append(apiKey, '');
          } else {
            filteredArray.forEach((item) => {
              formDataToSend.append(apiKey, item === null || item === undefined ? '' : String(item));
            });
          }
        } else if (typeof value === 'boolean') {
          formDataToSend.append(apiKey, value ? 'true' : 'false');
        } else {
          const s =
            value === null || value === undefined ? '' : String(value).trim();
          formDataToSend.append(apiKey, s);
        }
      });

      // Pass new image uploads as pictures (only once)
      formData.Pictures.forEach((file) => {
        formDataToSend.append('pictures', file);
      });

      // Pass kept existing image URLs as picturesKeep
      const keepUrls = (formData.existingBannerImages || [])
        .map((u) => String(u).trim())
        .filter(Boolean);
      keepUrls.forEach((url) => {
        formDataToSend.append('picturesKeep', url);
      });

      // Pass removed image URLs as picturesRemove
      const picturesRemove = formData.picturesRemove || [];
      picturesRemove.forEach((url) => {
        formDataToSend.append('picturesRemove', url);
      });

      // Handle Profile Image / Logo Picture (Multipart File upload)
      if (formData.profileImageUrlFile) {
        formDataToSend.append('logoPicture', formData.profileImageUrlFile);
        formDataToSend.append('LogoPicture', formData.profileImageUrlFile);
        formDataToSend.append('ProfileImageUrl', formData.profileImageUrlFile);
      } else if (formData.existingProfileImageUrl) {
        formDataToSend.append('logoPicture', formData.existingProfileImageUrl);
        formDataToSend.append('LogoPicture', formData.existingProfileImageUrl);
        formDataToSend.append('ProfileImageUrl', formData.existingProfileImageUrl);
      } else if (selectedContact && formData.existingProfileImageUrl === '') {
        formDataToSend.append('logoPictureRemove', 'true');
        formDataToSend.append('LogoPictureRemove', 'true');
        formDataToSend.append('ProfileImageUrlRemove', 'true');
      }

      let response;
      if (selectedContact) {
        formDataToSend.append('Id', selectedContact.id);
        response = await contactsAPI.updateContact(formDataToSend);
      } else {
        response = await contactsAPI.createContact(formDataToSend);
      }

      if (response?.data?.success === true) {
        showToast(selectedContact ? t('contacts.toastUpdated') : t('contacts.toastCreated'), 'success');
        handleClose();
        fetchContacts();
      } else {
        const apiMsg =
          response?.data?.errorMessage ||
          response?.data?.message ||
          (selectedContact ? t('contacts.toastUpdateError') : t('contacts.toastSaveError'));
        const parsed = parseContactApiErrors(apiMsg);
        setFormErrors(parsed);
        const firstErrorField = CONTACT_FIELD_ORDER.find((field) => parsed[field]);
        if (firstErrorField) {
          scrollToField(firstErrorField);
        }
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      const apiMsg =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        t('contacts.toastSaveError');
      const parsed = parseContactApiErrors(apiMsg);
      setFormErrors(parsed);
      const firstErrorField = CONTACT_FIELD_ORDER.find((field) => parsed[field]);
      if (firstErrorField) {
        scrollToField(firstErrorField);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (contactId) => {
    if (browserGlobal.confirm(t('contacts.confirmDelete'))) {
      setIsLoading(true);
      try {
        await contactsAPI.deleteContact(contactId);
        showToast(t('contacts.toastDeleted'), 'success');
        fetchContacts(filters.cityId, filters.categoryId);
      } catch (error) {
        console.error('Error deleting contact:', error);
        showToast(t('contacts.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const bannerImages = useMemo(() => {
    return (previewContact?.imageUrls || []).filter(Boolean);
  }, [previewContact]);

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">{t('contacts.title')}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.92rem',
              textTransform: 'none',
              px: 2.8,
              py: 1,
              borderRadius: '12px',
              boxShadow: '0 4px 14px rgba(15, 61, 62, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
                boxShadow: '0 6px 18px rgba(15, 61, 62, 0.35)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('contacts.add')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-end' },
            gap: 2,
            flexWrap: { md: 'nowrap' },
            overflowX: { md: 'auto' },
            pt: { md: 2.5 },
            pb: { md: 0.5 },
          }}
        >
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              minWidth: { md: 200 },
              flex: { md: '1 1 18%' },
              mt: 0,
              overflow: 'visible',
            }}
          >
            <InputLabel id="contacts-filter-city-label">{t('contacts.filterCity')}</InputLabel>
            <Select
              labelId="contacts-filter-city-label"
              value={filters.cityId}
              label={t('contacts.filterCity')}
              onChange={(e) => {
                const newCityId = e.target.value;
                setPage(0);
                if (newCityId) {
                  fetchCategories(newCityId);
                } else {
                  setFilters((prev) => ({
                    ...prev,
                    cityId: '',
                    categoryId: '',
                  }));
                  setCategories([]);
                }
              }}
            >
              <MenuItem value="">
              </MenuItem>
              {cities.map(city => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            disabled={!filters.cityId}
            sx={{
              minWidth: { md: 200 },
              flex: { md: '1 1 18%' },
              mt: 0,
              overflow: 'visible',
            }}
          >
            <InputLabel id="contacts-filter-category-label">{t('contacts.filterCategory')}</InputLabel>
            <Select
              labelId="contacts-filter-category-label"
              value={filters.categoryId}
              label={t('contacts.filterCategory')}
              onChange={(e) => {
                const newCategoryId = e.target.value;
                setFilters(prev => ({
                  ...prev,
                  categoryId: newCategoryId
                }));
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>{t('contacts.allCategories')}</em>
              </MenuItem>
              {categories
                .filter(category => category.hasContact === true || category.hasContact === 'true')
                .map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.title_EN}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ width: { md: 148 }, flexShrink: 0 }}>
            <FormControl fullWidth size="small" variant="outlined" disabled={!filters.cityId}>
              <Select
                id="contacts-filter-status-select"
                displayEmpty
                value={filters.isActive}
                inputProps={{ 'aria-label': t('promotions.filterStatus') }}
                renderValue={(selected) => {
                  if (selected === 'true') return t('common.active');
                  if (selected === 'false') return t('common.inactive');
                  return (
                    <Typography variant="body2" color="text.secondary" noWrap component="span">
                      {t('promotions.allStatusesHint')}
                    </Typography>
                  );
                }}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, isActive: e.target.value }));
                  setPage(0);
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            size="small"
            fullWidth
            disabled={!filters.cityId}
            label={t('promotions.searchTitle')}
            value={filters.title}
            onChange={(e) => setFilters((prev) => ({ ...prev, title: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: { md: 180 },
              flex: { md: '1 1 24%' },
            }}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
            onClick={() => {
              setFilters({
                title: '',
                cityId: '',
                categoryId: '',
                isActive: '',
              });
              setCategories([]);
              setPage(0);
              fetchCities();
            }}
            sx={{
              ...clearFilterButtonSx,
              alignSelf: { xs: 'flex-start', md: 'flex-end' },
              flexShrink: 0,
              whiteSpace: 'nowrap',
              width: { xs: '100%', md: 150 },
              minWidth: { xs: '100%', md: 150 },
              maxWidth: { xs: '100%', md: 150 },
              height: 36,
              minHeight: 36,
              maxHeight: 36,
              px: 1,
              fontSize: '0.8125rem',
              '& .MuiButton-startIcon': {
                marginRight: '4px',
                marginLeft: '-2px',
                '& .MuiSvgIcon-root': { fontSize: 18 },
              },
            }}
          >
            {t('common.clear')}
          </Button>
        </Box>
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 3,
            overflowX: 'auto',
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#ebf4f4'
            }
          }}
        >
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 140 }}>{t('common.title')}</TableCell>
                <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>{t('contacts.colInfo')}</TableCell>
                <TableCell sx={{ minWidth: 160 }}>{t('contacts.colLocation')}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>{t('contacts.colContactDetails')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colOwnerName') || 'Owner Name'}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colAddedByType') || 'Added By'}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colApprovedByName') || 'Approved By'}</TableCell>
                <TableCell sx={{ minWidth: 100 }}>{t('common.status')}</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleContacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2">{contact.title}</Typography>
                      {contact.contactPerson && (
                        <Typography variant="caption" color="textSecondary">
                          Contact: {contact.contactPerson}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="body2">{contact.description || '-'}</Typography>
                          {contact.infoNote && (
                            <Typography variant="caption" color="inherit" sx={{ mt: 0.5, display: 'block' }}>
                              Note: {contact.infoNote}
                            </Typography>
                          )}
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 280 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4
                          }}
                        >
                          {contact.description || '-'}
                        </Typography>
                        {contact.infoNote && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {contact.infoNote}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${contact.address || ''}${contact.district ? `, ${contact.district}` : ''}`} placement="top" arrow>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 200 }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {contact.address || '-'}
                          {contact.district && `, ${contact.district}`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {contact.phone && (
                        <Tooltip title={contact.phone}>
                          <Chip icon={<Phone />} label={contact.phone.length > 20 ? contact.phone.slice(0, 17) + '...' : contact.phone} size="small" variant="outlined" sx={{ maxWidth: 180 }} />
                        </Tooltip>
                      )}
                      {contact.email && (
                        <Tooltip title={contact.email}>
                          <Chip icon={<Email />} label={contact.email.length > 25 ? contact.email.slice(0, 22) + '...' : contact.email} size="small" variant="outlined" sx={{ maxWidth: 180 }} />
                        </Tooltip>
                      )}
                      {contact.website && (
                        <Tooltip title={contact.website}>
                          <Chip icon={<Language />} label={contact.website.length > 25 ? contact.website.slice(0, 22) + '...' : contact.website} size="small" variant="outlined" sx={{ maxWidth: 180 }} />
                        </Tooltip>
                      )}
                      {contact.whatsapp && (
                        <Tooltip title={contact.whatsapp}>
                          <Chip icon={<WhatsApp />} label={contact.whatsapp.length > 20 ? contact.whatsapp.slice(0, 17) + '...' : contact.whatsapp} size="small" variant="outlined" sx={{ maxWidth: 180 }} />
                        </Tooltip>
                      )}
                      {!contact.phone && !contact.email && !contact.website && !contact.whatsapp && (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {contact.ownerName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {contact.addedByType ? (
                      <Chip
                        label={contact.addedByType}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {contact.approvedByName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={contact.approvalStatus}
                          color={getApprovalStatusChipColor(contact.approvalStatus)}
                          size="small"
                        />
                        {contact.isActive && (
                          <Chip
                            label={t('common.active')}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {contact.approvalStatus === 'REJECTED' && contact.approvalNote && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {t('common.note')}: {contact.approvalNote}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton
                        sx={{ color: '#2196f3' }}
                        onClick={() => handleOpenPreview(contact)}
                        size="small"
                        title={t('common.mobileView') || 'Mobile View'}
                        disabled={isSubmitting}
                      >
                        <Smartphone />
                      </IconButton>
                      {isAddedByUser(contact) ? (
                        <IconButton
                          color="info"
                          onClick={() => handleOpen(contact, true)}
                          size="small"
                          title={t('common.view') || 'View'}
                          disabled={isSubmitting}
                        >
                          <Visibility />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="primary"
                          onClick={() => handleOpen(contact, false)}
                          size="small"
                          title={t('common.edit')}
                          disabled={isSubmitting}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(contact.id)}
                          size="small"
                          title={t('common.delete')}
                          disabled={isSubmitting}
                        >
                          <Delete />
                        </IconButton>
                      )}
                      {(contact.isActive === true || contact.IsActive === true) && (
                        <IconButton
                          color="warning"
                          onClick={() => openApprovalDialog(contact, contact.approvalStatus)}
                          size="small"
                          title={t('promotions.updateStatus') || 'Change Status'}
                          disabled={isSubmitting}
                        >
                          <PublishedWithChanges />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
          />
        </TableContainer>
      )}

      {/* Main Add/Edit Dialog */}
      <Dialog open={open} onClose={(event, reason) => reason === 'backdropClick' ? null : handleClose()} maxWidth="md" fullWidth fullScreen={isMobileView}>
        <DialogTitle>
          {isViewMode ? t('contacts.dialogView') : (selectedContact ? t('contacts.dialogEdit') : t('contacts.dialogAdd'))}
        </DialogTitle>
        <DialogContent ref={dialogContentRef}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} data-field="cityId">
                <FormControl fullWidth required error={Boolean(formErrors.cityId)}>
                  <InputLabel id="contact-form-city-label">{t('common.city')}</InputLabel>
                  <Select
                    labelId="contact-form-city-label"
                    name="cityId"
                    value={formData.cityId}
                    label={`${t('common.city')} *`}
                    readOnly={isViewMode}
                    onChange={(e) => {
                      const newCityId = e.target.value;
                      setFormErrors((prev) => ({
                        ...prev,
                        cityId: '',
                        categoryId: '',
                        _form: '',
                      }));
                      setFormData((prev) => ({
                        ...prev,
                        cityId: newCityId,
                        categoryId: '',
                        districtId: '',
                      }));
                      if (newCityId) {
                        fetchCategories(newCityId, true);
                      } else {
                        setCategories([]);
                        setDistricts([]);
                      }
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.cityId ? <FormHelperText>{formErrors.cityId}</FormHelperText> : null}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} data-field="categoryId">
                <FormControl fullWidth required disabled={!formData.cityId} error={Boolean(formErrors.categoryId)}>
                  <InputLabel id="contact-form-category-label">{t('contacts.fieldCategory')}</InputLabel>
                  <Select
                    labelId="contact-form-category-label"
                    name="categoryId"
                    value={formData.categoryId}
                    label={`${t('contacts.fieldCategory')} *`}
                    readOnly={isViewMode}
                    onChange={handleChange}
                  >
                    {categories
                      .filter(category => category.hasContact === true || category.hasContact === 'true')
                      .map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.title_EN}
                        </MenuItem>
                      ))}
                  </Select>
                  {formErrors.categoryId ? <FormHelperText>{formErrors.categoryId}</FormHelperText> : null}
                </FormControl>
              </Grid>

              <Grid item xs={12} data-field="title">
                <TextField
                  fullWidth
                  label={t('common.title')}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  error={Boolean(formErrors.title)}
                  helperText={formErrors.title || undefined}
                  InputProps={{ readOnly: isViewMode }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              <Grid item xs={12} data-field="description">
                <TextField
                  fullWidth
                  label={t('common.description')}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  error={Boolean(formErrors.description)}
                  helperText={formErrors.description || undefined}
                  InputProps={{ readOnly: isViewMode }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              <Grid item xs={12} data-field="infoNote">
                <TextField
                  fullWidth
                  label={`${t('contacts.fieldInfoNote')} *`}
                  name="infoNote"
                  value={formData.infoNote}
                  onChange={handleChange}
                  error={Boolean(formErrors.infoNote)}
                  helperText={formErrors.infoNote || undefined}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="contactType">
                <FormControl fullWidth required error={Boolean(formErrors.contactType)}>
                  <InputLabel id="contact-form-contact-type-label">Contact Type</InputLabel>
                  <Select
                    labelId="contact-form-contact-type-label"
                    name="contactType"
                    value={formData.contactType}
                    label="Contact Type *"
                    readOnly={isViewMode}
                    onChange={handleChange}
                  >
                    <MenuItem value="Company">Company</MenuItem>
                    <MenuItem value="Freelancer">Freelancer</MenuItem>
                  </Select>
                  {formErrors.contactType ? <FormHelperText>{formErrors.contactType}</FormHelperText> : null}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} data-field="contactPerson">
                <TextField
                  fullWidth
                  label={t('contacts.fieldContactPerson')}
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  InputProps={{ readOnly: isViewMode }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="callToAction">
                <TextField
                  fullWidth
                  label="Call to Action"
                  name="callToAction"
                  value={formData.callToAction || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: isViewMode }}
                  inputProps={{ maxLength: 30 }}
                  error={Boolean(formErrors.callToAction)}
                  helperText={formErrors.callToAction || `${(formData.callToAction || '').length}/30`}
                  FormHelperTextProps={{
                    sx: { textAlign: 'right' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="address">
                <Autocomplete
                  freeSolo
                  loading={addressLoading}
                  options={addressSuggestions}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.address?.label || ''}
                  filterOptions={(x) => x}
                  value={formData.address || ''}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const typeofStr = typeof newValue === 'string';
                      const addr = typeofStr ? {} : (newValue.address || {});

                      let addressStr = '';
                      if (typeofStr) {
                        addressStr = newValue;
                      } else if (addr.street) {
                        addressStr = addr.street;
                      } else {
                        addressStr = addr.label || '';
                      }

                      const houseNumberStr = addr.houseNumber || '';
                      const postalCodeStr = addr.postalCode || '';

                      let lat = '';
                      let lng = '';
                      if (!typeofStr && newValue.position) {
                        lat = newValue.position.lat;
                        lng = newValue.position.lng;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        address: addressStr,
                        houseNumber: houseNumberStr || prev.houseNumber,
                        postalCode: postalCodeStr || prev.postalCode,
                        latitude: lat,
                        longitude: lng
                      }));

                      if (lat && lng) {
                        setFormErrors((prev) => ({
                          ...prev,
                          address: '',
                          houseNumber: '',
                          postalCode: '',
                          _form: ''
                        }));
                      } else {
                        setFormErrors((prev) => ({
                          ...prev,
                          address: addressStr ? 'Please select address from auto-fetch suggestions' : (t('contacts.errFieldRequired') || 'Required'),
                          _form: ''
                        }));
                      }
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        address: '',
                        houseNumber: '',
                        postalCode: '',
                        latitude: '',
                        longitude: ''
                      }));
                      setFormErrors((prev) => ({
                        ...prev,
                        address: t('contacts.errFieldRequired') || 'Required',
                        _form: ''
                      }));
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    const sliced = newInputValue || '';
                    if (reason === 'input' || reason === 'clear') {
                      setFormData((prev) => ({
                        ...prev,
                        address: sliced,
                        latitude: '',
                        longitude: ''
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        address: sliced
                      }));
                    }
                    handleAddressSearch(sliced);
                  }}
                  readOnly={isViewMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.address') || 'Address'}
                      required
                      error={Boolean(formErrors.address)}
                      helperText={formErrors.address || undefined}
                      onBlur={(e) => {
                        if (params.inputProps && typeof params.inputProps.onBlur === 'function') {
                          params.inputProps.onBlur(e);
                        }
                        const current = formDataRef.current;
                        const addrStr = String(current?.address || '').trim();
                        const latVal = current?.latitude;
                        const lngVal = current?.longitude;

                        if (!addrStr) {
                          setFormErrors((prev) => ({ ...prev, address: t('contacts.errFieldRequired') || 'Required' }));
                        } else if (!latVal || !lngVal) {
                          setFormErrors((prev) => ({ ...prev, address: 'Please select address from auto-fetch suggestions' }));
                        } else {
                          setFormErrors((prev) => ({ ...prev, address: '' }));
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {addressLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="districtId">
                <FormControl fullWidth required disabled={!formData.cityId} error={Boolean(formErrors.districtId)}>
                  <InputLabel>{`${t('common.district')} `}</InputLabel>
                  <Select
                    name="districtId"
                    value={formData.districtId || ''}
                    label={`${t('common.district')} *`}
                    readOnly={isViewMode}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {districts.map(district => (
                      <MenuItem key={district.id} value={district.id}>
                        {district.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.districtId && <FormHelperText>{formErrors.districtId}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} data-field="houseNumber">
                <TextField
                  fullWidth
                  label={t('common.houseNumber')}
                  name="houseNumber"
                  value={formData.houseNumber}
                  onChange={handleChange}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="postalCode">
                <TextField
                  fullWidth
                  label={`${t('common.postalCode')} *`}
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder={t('contacts.placeholderPostal5')}
                  error={Boolean(formErrors.postalCode)}
                  helperText={formErrors.postalCode || undefined}
                  InputProps={{ readOnly: isViewMode }}
                  inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>

              <Grid item xs={12} data-field="openingHours">
                <BusinessHoursInput
                  value={formData.openingHours}
                  onChange={(val) => {
                    setFormErrors((prev) => ({ ...prev, openingHours: '', _form: '' }));
                    setFormData((prev) => ({ ...prev, openingHours: val }));
                  }}
                  readOnly={isViewMode}
                  error={Boolean(formErrors.openingHours)}
                  helperText={formErrors.openingHours}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="email">
                <TextField
                  fullWidth
                  label={t('common.email')}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email || undefined}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="phone">
                <TextField
                  fullWidth
                  label={t('common.phone')}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={Boolean(formErrors.phone)}
                  helperText={formErrors.phone || undefined}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="website">
                <TextField
                  fullWidth
                  label={t('common.website')}
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  error={Boolean(formErrors.website)}
                  helperText={formErrors.website || undefined}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field="whatsapp">
                <TextField
                  fullWidth
                  label={t('common.whatsapp')}
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  error={Boolean(formErrors.whatsapp)}
                  helperText={formErrors.whatsapp || t('contacts.helperWhatsappMax20')}
                  FormHelperTextProps={
                    formErrors.whatsapp ? undefined : { sx: { color: 'text.secondary' } }
                  }
                  inputProps={{ maxLength: 20 }}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} data-field="whatsappText">
                <TextField
                  fullWidth
                  label={t('common.whatsAppText')}
                  name="whatsappText"
                  value={formData.whatsappText}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  error={Boolean(formErrors.whatsappText)}
                  helperText={formErrors.whatsappText || undefined}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              {/* Coordinates are auto-filled behind the scenes and hidden from the frontend */}

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 1 }}>
                  Logo Image
                </Typography>

                <Box sx={{
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  minHeight: '100px',
                  mb: 2
                }}>
                  <Grid container spacing={2}>
                    {/* Logo Preview */}
                    {(formData.profileImageUrlFile || formData.existingProfileImageUrl) && (
                      <Grid item>
                        <Box sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          <img
                            src={formData.profileImageUrlFile ? URL.createObjectURL(formData.profileImageUrlFile) : formData.existingProfileImageUrl}
                            alt="Logo Preview"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          {!isViewMode && (
                            <IconButton
                              size="small"
                              className="delete-btn"
                              onClick={handleRemoveProfileImage}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#fff',
                                opacity: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                p: 0.5
                              }}
                            >
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* Logo Upload Button */}
                    {!isViewMode && !formData.profileImageUrlFile && !formData.existingProfileImageUrl && (
                      <Grid item>
                        <Button
                          component="label"
                          sx={{
                            width: 80,
                            height: 80,
                            border: '2px dashed #d1d5db',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6b7280',
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#0F3D3E',
                              color: '#0F3D3E',
                              bgcolor: 'rgba(124, 58, 237, 0.04)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <AddPhotoAlternate sx={{ fontSize: 28 }} />
                          <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>Upload Logo</Typography>
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleProfileImageFileChange}
                          />
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 1 }}>
                  Banner Image
                </Typography>

                <Box sx={{
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  minHeight: '100px'
                }}>
                  <Grid container spacing={2}>
                    {/* Existing Images */}
                    {formData.existingBannerImages.map((url, idx) => (
                      <Grid item key={`existing-${idx}`}>
                        <Box sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          <img
                            src={url}
                            alt="Existing"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          {!isViewMode && (
                            <IconButton
                              size="small"
                              className="delete-btn"
                              onClick={() => handleRemoveImage(idx, true)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#fff',
                                opacity: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                p: 0.5
                              }}
                            >
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}

                    {/* New Selected Images */}
                    {formData.Pictures.map((file, idx) => (
                      <Grid item key={`new-${idx}`}>
                        <Box sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt="New"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          {!isViewMode && (
                            <IconButton
                              size="small"
                              className="delete-btn"
                              onClick={() => handleRemoveImage(idx)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#fff',
                                opacity: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                p: 0.5
                              }}
                            >
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}

                    {/* Add Image Button */}
                    {!isViewMode && (
                      <Grid item>
                        <Button
                          component="label"
                          sx={{
                            width: 80,
                            height: 80,
                            border: '2px dashed #d1d5db',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6b7280',
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#0F3D3E',
                              color: '#0F3D3E',
                              bgcolor: 'rgba(124, 58, 237, 0.04)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <AddPhotoAlternate sx={{ fontSize: 28 }} />
                          <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>{t('common.add') || 'Add'}</Typography>
                          <input
                            type="file"
                            hidden
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} data-field="supportLanguages">
                <FormControl fullWidth required error={Boolean(formErrors.supportLanguages)}>
                  <InputLabel id="contacts-form-languages-label">
                    {`${t('common.supportLanguages') || 'Support Languages'} `}
                  </InputLabel>
                  <Select
                    labelId="contacts-form-languages-label"
                    name="supportLanguages"
                    multiple
                    value={normalizeCommaSeparated(formData.supportLanguages) || []}
                    label={`${t('common.supportLanguages') || 'Support Languages'} *`}
                    readOnly={isViewMode}
                    onChange={handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {languagesList.map((lang) => (
                      <MenuItem key={lang.id || lang.languageId || lang.name} value={lang.name}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.supportLanguages && <FormHelperText>{formErrors.supportLanguages}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} data-field="clientTypes">
                <TextField
                  fullWidth
                  label={t('common.clientTypes')}
                  name="clientTypes"
                  value={formData.clientTypes}
                  onChange={handleChange}
                  InputProps={{ readOnly: isViewMode }}
                />
              </Grid>

              <Grid item xs={12} data-field="subCategoryIds">
                <FormControl
                  fullWidth
                  disabled={!formData.categoryId}
                  error={Boolean(formErrors.subCategoryIds)}
                >
                  <InputLabel id="contact-form-subcats-label">
                    {`${t('contacts.fieldSubCategoriesComma') || 'Sub Categories'} *`}
                  </InputLabel>
                  <Select
                    labelId="contact-form-subcats-label"
                    name="subCategoryIds"
                    multiple
                    value={formData.subCategoryIds || []}
                    label={`${t('contacts.fieldSubCategoriesComma') || 'Sub Categories'} *`}
                    readOnly={isViewMode}
                    onChange={handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={subCategoriesList.find(s => s.id === value)?.name_EN ||
                              subCategoriesList.find(s => s.id === value)?.title_EN ||
                              subCategoriesList.find(s => s.id === value)?.name ||
                              value}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {subCategoriesList.map(subCat => (
                      <MenuItem key={subCat.id} value={subCat.id}>
                        {subCat.name_EN || subCat.title_EN || subCat.title || subCat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.subCategoryIds ? (
                    <FormHelperText>{formErrors.subCategoryIds}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>

              {renderArrayField(t('contacts.documentsRequired') || 'Documents Required', 'documentsRequired')}
              {renderArrayField(t('contacts.rulesRegulations') || 'Terms & Conditions', 'rulesRegulations')}
              {renderPricingInformationSection()}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {canAddEdit && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleChange({
                            target: {
                              name: 'isActive',
                              value: e.target.checked
                            }
                          })}
                          sx={{
                            pointerEvents: isViewMode ? 'none' : 'auto',
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F3D3E' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F3D3E' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t('common.active')}
                        </Typography>
                      }
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isGoogleRatingAvailable}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isGoogleRatingAvailable',
                            value: e.target.checked
                          }
                        })}
                        sx={{
                          pointerEvents: isViewMode ? 'none' : 'auto',
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F3D3E' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F3D3E' }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('common.googleRating')}
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
          {formErrors._form ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formErrors._form}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{isViewMode ? t('common.close') || 'Close' : t('common.cancel')}</Button>
          {!isViewMode && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              sx={{
                background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)'
                }
              }}
            >
              {getContactSubmitLabel(selectedContact, t)}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('contacts.approvalDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={approvalStatus}
                label={t('common.status')}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <MenuItem value={ApprovalStatus.APPROVED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.APPROVED]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.REJECTED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="error" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.REJECTED]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.PENDING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingOutlined color="warning" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.PENDING]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.INACTIVE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="disabled" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.INACTIVE]}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={approvalStatus === ApprovalStatus.REJECTED ? t('contacts.fieldRejectionNoteShort') : t('contacts.fieldApprovalNoteShort')}
              name="approvalNote"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              multiline
              rows={4}
              required={approvalStatus === ApprovalStatus.REJECTED}
              error={approvalStatus === ApprovalStatus.REJECTED && !approvalNote.trim()}
              helperText={approvalStatus === ApprovalStatus.REJECTED && !approvalNote.trim() ? t('contacts.helperRejectionRequired') : ''}
              placeholder={approvalStatus === ApprovalStatus.REJECTED ? t('accommodation.placeholderReject') : t('accommodation.placeholderOptional')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color={getApprovalDialogButtonColor(approvalStatus)}
            disabled={isSubmitting || (approvalStatus === ApprovalStatus.REJECTED && !approvalNote.trim())}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? t('common.updating') : t('promotions.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>

      {/* Mobile Mockup Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            backgroundImage: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 0,
            m: 0,
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Close button floating next to the smartphone bezel on desktop, fixed on mobile */}
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: { xs: 'fixed', sm: 'absolute' },
              top: { xs: 10, sm: 10 },
              left: { xs: 'auto', sm: 'calc(50% + 155px)' },
              right: { xs: 10, sm: 'auto' },
              bgcolor: { xs: 'rgba(255,255,255,0.8)', sm: '#1e293b' },
              color: { xs: '#000', sm: '#ffffff' },
              '&:hover': {
                bgcolor: { xs: 'rgba(255,255,255,1)', sm: '#0f172a' }
              },
              zIndex: 2000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Smartphone Bezel on desktop */}
          <Box
            sx={{
              width: { xs: '100%', sm: '290px' },
              height: { xs: '100vh', sm: '650px' },
              maxHeight: { xs: '100vh', sm: '85vh' },
              borderRadius: { xs: 0, sm: '32px' },
              border: { xs: 'none', sm: '6px solid #1e293b' },
              boxShadow: { xs: 'none', sm: '0 20px 40px -10px rgba(0,0,0,0.4)' },
              bgcolor: '#1e293b',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Smartphone Inner Screen */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#f8fafc',
                overflow: 'hidden',
                position: 'relative',
                pt: 0
              }}
            >
              {/* Phone Header */}
              <Box
                sx={{
                  height: '56px',
                  bgcolor: '#073032',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  color: '#ffffff',
                  zIndex: 99,
                  flexShrink: 0
                }}
              >
                <IconButton
                  onClick={() => setPreviewOpen(false)}
                  sx={{ color: '#0bbba1', p: 0, mr: 0.5 }}
                >
                  <KeyboardArrowLeft sx={{ fontSize: 24 }} />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#ffffff',
                    fontSize: '1.05rem',
                    flex: 1
                  }}
                >
                  {previewContact?.title || 'Preview'}
                </Typography>
              </Box>

              {/* Scrollable Screen Content */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  pb: '80px',
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                }}
              >
                {/* Banner Carousel */}
                <Box sx={{ width: '100%', height: '190px', position: 'relative', bgcolor: '#e2e8f0' }}>
                  {bannerImages && bannerImages.length > 0 ? (
                    <>
                      <Box
                        component="img"
                        src={bannerImages[activeBannerIndex]}
                        alt="banner"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {bannerImages.length > 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 12,
                            right: 12,
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            px: 1,
                            py: 0.25,
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {activeBannerIndex + 1}/{bannerImages.length}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#e2e8f0',
                        color: '#64748b'
                      }}
                    >
                      <Typography variant="body2">No Banner Image</Typography>
                    </Box>
                  )}
                </Box>

                {/* Overlapping Info Card */}
                <CardSection previewContact={previewContact} />

                {/* Action Grid Buttons */}
                <ActionsGrid previewContact={previewContact} />

                {/* Independent Listing Warning */}
                <WarningBox />

                {/* About Section */}
                {previewContact?.description && (
                  <ContentSection title={t('about') || 'About'}>
                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.8rem' }}>
                      {previewContact.description}
                    </Typography>
                  </ContentSection>
                )}

                {/* Services & Info Section */}
                <ServicesSection previewContact={previewContact} subCategoriesList={subCategoriesList} t={t} />

                {/* Required Documents Section */}
                {previewContact?.documentsRequired && previewContact.documentsRequired.length > 0 && (
                  <ListSection
                    title="REQUIRED DOCUMENTS"
                    items={previewContact.documentsRequired}
                    icon={<Description sx={{ fontSize: 16 }} />}
                  />
                )}

                {/* Terms & Conditions Section */}
                {previewContact?.rulesRegulations && previewContact.rulesRegulations.length > 0 && (
                  <ListSection
                    title="TERMS & CONDITIONS"
                    items={previewContact.rulesRegulations}
                    icon={<Gavel sx={{ fontSize: 16 }} />}
                  />
                )}

                {/* Pricing / Fees Section */}
                {previewContact?.pricingInformation && (
                  (previewContact.pricingInformation.headerTitle ||
                    previewContact.pricingInformation.headerDescription ||
                    previewContact.pricingInformation.plans?.length > 0) && (
                  <PricingPreviewSection
                    title={previewContact.pricingInformation.headerTitle || 'PRICING / FEES'}
                    headerDescription={previewContact.pricingInformation.headerDescription}
                    items={previewContact.pricingInformation.plans || []}
                    icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
                  />
                  )
                )}

                {/* Location Map visual Section */}
                <LocationSection previewContact={previewContact} />

              </Box>

              {/* Bottom Enquiry Bar */}
              <BottomBar previewContact={previewContact} />

            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

// Helper Components for Mobile Mockup Preview
const ContentSection = ({ title, children }) => {
  return (
    <Box sx={{ px: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ width: 4, height: 20, bgcolor: '#073032', borderRadius: '2px', mr: 1.5 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#073032', fontSize: '0.85rem' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

const InfoNoteBox = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const isLong = text.length > 80;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: '12px',
        bgcolor: '#fef2f2', // soft red/pink-50
        border: '1.5px solid #fecaca', // red-200
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <InfoOutlined sx={{ color: '#ef4444', fontSize: 18, mt: 0.25 }} />
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Typography
          variant="body2"
          sx={{
            color: '#b91c1c', // red-700
            fontSize: '0.8rem',
            lineHeight: 1.4,
            ...((isLong && !expanded) && {
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              pr: 9, // padding to clear the absolute button
            }),
          }}
        >
          {text}
          {isLong && expanded && (
            <Box
              component="span"
              onClick={(e) => {
                e.preventDefault();
                setExpanded(false);
              }}
              sx={{
                color: '#b91c1c',
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 0.5,
                display: 'inline-block',
                '&:hover': {
                  color: '#991b1b',
                }
              }}
            >
              Read less
            </Box>
          )}
        </Typography>

        {isLong && !expanded && (
          <Box
            component="span"
            onClick={(e) => {
              e.preventDefault();
              setExpanded(true);
            }}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              color: '#b91c1c',
              fontWeight: 700,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.8rem',
              bgcolor: '#fef2f2', // match container background
              pl: 0.5,
              '&:hover': {
                color: '#991b1b',
              }
            }}
          >
            ... Read more
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CardSection = ({ previewContact }) => {
  const isVerified = previewContact?.approvalStatus === 'APPROVED';
  const initials = previewContact?.title
    ? previewContact.title.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : 'C';

  return (
    <Box sx={{ px: 0, mt: -4, position: 'relative', zIndex: 10 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '24px 24px 0 0',
          bgcolor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              border: '3px solid #ffffff',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              bgcolor: '#073032',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {previewContact?.profileImageUrl || previewContact?.logoPicture ? (
              <Box
                component="img"
                src={previewContact.profileImageUrl || previewContact.logoPicture}
                alt="Logo"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.9rem' }}>
                {initials}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Verification row above title */}
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, mb: 1, overflow: 'hidden' }}>
              {isVerified && (
                <Chip
                  label="✓ VERIFIED"
                  size="small"
                  sx={{
                    bgcolor: '#ecfdf5',
                    color: '#10b981',
                    border: '1px solid #a7f3d0',
                    fontWeight: 800,
                    fontSize: '0.6rem',
                    height: 18,
                    borderRadius: '9px',
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 0.8 }
                  }}
                />
              )}
            </Box>

            {/* Title with CheckCircle badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.95rem'
                }}
              >
                {previewContact?.title || 'Company Title'}
              </Typography>
              {isVerified && (
                <CheckCircle sx={{ color: '#10b981', fontSize: 16, flexShrink: 0 }} />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2, gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <LocationOn sx={{ color: '#ef4444', fontSize: 18, mt: 0.25 }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, lineHeight: 1.3 }}>
              {previewContact?.address ? `${previewContact.address}${previewContact.houseNumber ? ' ' + previewContact.houseNumber : ''}, ${previewContact.postalCode || ''} Berlin, Germany` : 'Berlin, Germany'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 800, whiteSpace: 'nowrap' }}>
            2.1 km
          </Typography>
        </Box>

        {/* Call to Action Box */}
        {previewContact?.callToAction && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: '16px',
              bgcolor: '#fffbeb', // soft amber/orange
              border: '1.5px solid #fb923c', // orange/amber-400
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              overflow: 'hidden',
            }}
          >
            <Campaign sx={{ color: '#ea580c', fontSize: 20, flexShrink: 0 }} />
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: '#c2410c', // orange-700
                fontWeight: 700,
                fontSize: '0.72rem',
                lineHeight: 1.3,
                textAlign: 'center',
                minWidth: 0,
              }}
            >
              {previewContact.callToAction}
            </Typography>
          </Box>
        )}

        {/* Info Note Box */}
        {previewContact?.infoNote && (
          <InfoNoteBox text={previewContact.infoNote} />
        )}
      </Paper>
    </Box>
  );
};

const ActionsGrid = ({ previewContact }) => {
  const actions = [
    { label: 'Call', icon: <Phone />, url: previewContact?.phone ? `tel:${previewContact.phone}` : null },
    { label: 'Website', icon: <Language />, url: previewContact?.website || null, target: '_blank' },
    { label: 'Email', icon: <Email />, url: previewContact?.email ? `mailto:${previewContact.email}` : null },
    { label: 'Directions', icon: <Map />, url: previewContact?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(previewContact.address)}` : null, target: '_blank' },
    { label: 'Chat', icon: <Chat />, url: '#' },
    { label: 'Book Now', icon: <CalendarMonth />, url: '#', enabled: previewContact?.isBookingAppointmentEnabled || previewContact?.isOnlineBookingEnabled || previewContact?.isOfflineBookingEnabled }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {actions.map((act, index) => {
          const disabled = act.enabled === false || !act.url;
          return (
            <Grid item xs={4} key={index}>
              <Box
                component={disabled ? 'div' : 'a'}
                href={disabled ? undefined : act.url}
                target={act.target}
                rel={act.target ? 'noopener noreferrer' : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'default' : 'pointer',
                  '&:hover': disabled ? {} : {
                    '& .icon-box': { bgcolor: '#e2e8f0' }
                  }
                }}
              >
                <Box
                  className="icon-box"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: '#f1f5f9',
                    color: '#073032',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s',
                    mb: 1,
                    '& svg': {
                      fontSize: 20
                    }
                  }}
                >
                  {act.icon}
                </Box>
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
                  {act.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const WarningBox = () => {
  return (
    <Box sx={{ px: 2, mb: 2 }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '12px',
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.4, textAlign: 'center' }}>
          <strong>Independent listing.</strong> Welivin is not affiliated with this business. Info sourced from public sources or the provider.
        </Typography>
      </Box>
    </Box>
  );
};

const ServicesSection = ({ previewContact, subCategoriesList, t }) => {
  const languages = Array.isArray(previewContact?.supportLanguages)
    ? previewContact.supportLanguages
    : typeof previewContact?.supportLanguages === 'string'
      ? previewContact.supportLanguages.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  const subCatNames = (previewContact?.subCategoryIds || []).map(id => {
    const subCat = subCategoriesList.find(s => s.id === id);
    return subCat ? (subCat.name_EN || subCat.title_EN || subCat.name) : `Subcategory ${id}`;
  }).filter(Boolean);

  const [showAllHours, setShowAllHours] = useState(false);

  return (
    <ContentSection title="Services & Info">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {languages.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  bgcolor: '#ffebee',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Translate sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#1e293b',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}
              >
                SUPPORTED LANGUAGES
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {languages.map((lang, idx) => (
                <Chip
                  key={idx}
                  label={lang}
                  size="small"
                  sx={{
                    bgcolor: '#ffffff',
                    color: '#1e293b',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    height: 28,
                    border: '1px solid #e2e8f0'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {subCatNames.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  bgcolor: '#ffebee',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Apps sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#1e293b',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}
              >
                CATEGORIES
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {subCatNames.map((name, idx) => (
                <Chip
                  key={idx}
                  label={name}
                  size="small"
                  sx={{
                    bgcolor: '#ffffff',
                    color: '#1e293b',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    height: 28,
                    border: '1px solid #e2e8f0'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {previewContact?.openingHours && (() => {
          const parsedHours = parseOpeningHoursString(previewContact.openingHours);
          const mondayData = parsedHours.find(d => d.day === 'Monday') || parsedHours[0];
          return (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: mondayData.isOpen ? '#10b981' : '#ef4444', borderRadius: '50%' }} />
                  <Typography variant="caption" sx={{ color: mondayData.isOpen ? '#10b981' : '#ef4444', fontWeight: 800 }}>
                    {mondayData.isOpen ? 'Open' : 'Closed'}
                  </Typography>
                </Box>
                {mondayData.isOpen && mondayData.slots?.[0]?.to && (
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Closes at {mondayData.slots[0].to}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ color: '#f59e0b', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    Monday
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 800 }}>
                  {formatDaySummary(mondayData)}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowAllHours(!showAllHours)}
                  sx={{
                    color: '#0f172a',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    py: 0
                  }}
                >
                  {showAllHours ? 'Hide opening hours ▲' : 'Show opening hours ▼'}
                </Button>
              </Box>
              {showAllHours && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1, borderTop: '1px solid #f1f5f9', pt: 1 }}>
                  {parsedHours.map((dayData) => (
                    <Box key={dayData.day} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{dayData.day}</Typography>
                      <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600 }}>
                        {formatDaySummary(dayData)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })()}
      </Box>
    </ContentSection>
  );
};

const renderFormattedItemText = (text) => {
  if (!text) return null;
  const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/gi;
  const parts = String(text).split(urlRegex);

  return parts.map((part, index) => {
    const isUrl = /^https?:\/\//i.test(part) || /^www\./i.test(part);
    if (isUrl) {
      const href = /^www\./i.test(part) ? `https://${part}` : part;
      return (
        <Typography
          key={index}
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            color: '#0F3D3E',
            fontSize: '0.85rem',
            lineHeight: 1.4,
            textDecoration: 'underline',
            wordBreak: 'break-all',
            '&:hover': { color: '#1b5a5c' }
          }}
        >
          {part}
        </Typography>
      );
    }
    return (
      <Typography
        key={index}
        component="span"
        variant="body2"
        sx={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.4, wordBreak: 'break-word' }}
      >
        {part}
      </Typography>
    );
  });
};

const SUGGESTED_BADGES = ['Most Popular', 'Flexible', 'Best Value', 'Recommended', 'Limited Offer'];

const PricingPlanCard = ({
  plan,
  index,
  isViewMode,
  onChange,
  onRemove,
  onAddHighlight,
  onEditHighlight,
  onRemoveHighlight,
  t,
  errors = {},
}) => {
  const [newHighlight, setNewHighlight] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const getPeriodSelectValue = (period) => {
    if (!period) return '/ month';
    const p = String(period).trim().toLowerCase();
    if (p === '/ week' || p === '/week' || p === 'week' || p === 'weekly' || p === 'week (/ week)') return '/ week';
    if (p === '/ year' || p === '/year' || p === 'year' || p === 'yearly' || p === 'annually' || p === 'year (/ year)') return '/ year';
    if (p === 'one-time' || p === 'one time' || p === 'once') return 'One-time';
    return '/ month';
  };

  const handlePeriodSelectChange = (e) => {
    onChange(index, 'period', e.target.value);
  };

  const handleAddHl = () => {
    if (!newHighlight.trim()) return;
    onAddHighlight(index, newHighlight.trim());
    setNewHighlight('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddHl();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        bgcolor: '#ffffff',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#cbd5e1',
          boxShadow: '0 4px 12px rgba(15, 61, 62, 0.05)',
        },
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          bgcolor: '#f8fafc',
          borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={`${t('contacts.pricingPlan') || 'Plan'} ${index + 1}`}
            size="small"
            sx={{
              bgcolor: '#0F3D3E',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: plan.title ? '#0f172a' : '#94a3b8', fontStyle: plan.title ? 'normal' : 'italic' }}>
            {plan.title || t('contacts.planTitle') || 'Plan Title'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          {!isViewMode && (
            <Tooltip title="Delete Plan">
              <IconButton
                size="small"
                color="error"
                onClick={() => onRemove(index)}
                sx={{
                  color: '#ef4444',
                  '&:hover': { bgcolor: '#fee2e2' },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            {/* Title & Badge */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label={`${t('contacts.planTitle') || 'Plan Title'} *`}
                placeholder="e.g., Student Health Insurance"
                value={plan.title || ''}
                onChange={(e) => onChange(index, 'title', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 50 }}
                error={Boolean(errors.title)}
                helperText={errors.title || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id={`plan-badge-label-${index}`}>{t('contacts.badgeTag') || 'Badge / Tag'}</InputLabel>
                <Select
                  labelId={`plan-badge-label-${index}`}
                  value={plan.badge || ''}
                  label={t('contacts.badgeTag') || 'Badge / Tag'}
                  onChange={(e) => onChange(index, 'badge', e.target.value)}
                  readOnly={isViewMode}
                >
                  <MenuItem value="">{t('contacts.noBadge') || 'No badge'}</MenuItem>
                  {plan.badge && !SUGGESTED_BADGES.includes(plan.badge) && (
                    <MenuItem value={plan.badge}>{plan.badge}</MenuItem>
                  )}
                  {SUGGESTED_BADGES.map((badgeLabel) => (
                    <MenuItem key={badgeLabel} value={badgeLabel}>{badgeLabel}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Price, Period, Hint */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t('contacts.price') || 'Price'}
                placeholder="e.g., €125 or 1.25"
                value={plan.price || ''}
                onChange={(e) => onChange(index, 'price', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 12, inputMode: 'decimal' }}
                error={Boolean(errors.price)}
                helperText={errors.price || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id={`billing-period-label-${index}`}>{t('contacts.billingPeriod') || 'Billing Period'}</InputLabel>
                <Select
                  labelId={`billing-period-label-${index}`}
                  value={getPeriodSelectValue(plan.period)}
                  label={t('contacts.billingPeriod') || 'Billing Period'}
                  readOnly={isViewMode}
                  renderValue={(value) => ({
                    '/ month': t('contacts.periodMonth') || 'Month',
                    '/ week': t('contacts.periodWeek') || 'Week',
                    '/ year': t('contacts.periodYear') || 'Year',
                    'One-time': t('contacts.periodOneTime') || 'One-time',
                  }[value] || value)}
                  onChange={handlePeriodSelectChange}
                >
                  <MenuItem value="/ month">{t('contacts.periodMonth') || 'Month'}</MenuItem>
                  <MenuItem value="/ week">{t('contacts.periodWeek') || 'Week'}</MenuItem>
                  <MenuItem value="/ year">{t('contacts.periodYear') || 'Year'}</MenuItem>
                  <MenuItem value="One-time">{t('contacts.periodOneTime') || 'One-time'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t('contacts.hintNote') || 'Hint / Note'}
                placeholder="e.g., Starts from €125/mo • Free incoming insurance"
                value={plan.hint || ''}
                onChange={(e) => onChange(index, 'hint', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 250 }}
                error={Boolean(errors.hint)}
                helperText={errors.hint || undefined}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label={t('contacts.description') || 'Description'}
                placeholder="Statutory & expat health cover fully compliant with German student visa requirements & university enrolment."
                value={plan.description || ''}
                onChange={(e) => onChange(index, 'description', e.target.value)}
                InputProps={{ readOnly: isViewMode }}
                inputProps={{ maxLength: 500 }}
                required
                error={Boolean(errors.description)}
                helperText={(
                  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>{errors.description || ''}</span>
                    <span>{(plan.description || '').length}/500</span>
                  </Box>
                )}
              />
            </Grid>

            {/* Highlights List */}
            <Grid item xs={12}>
              <Box sx={{ mt: 0.5, p: 2, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 16, color: '#0F3D3E' }} />
                    {t('contacts.planHighlights') || 'Plan Highlights & Features'}
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 400 }}>
                      ({(plan.highlights || []).length} items)
                    </Typography>
                  </Typography>
                </Box>

                {/* Highlights Items */}
                {(plan.highlights || []).length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                    {(plan.highlights || []).map((hl, hlIdx) => (
                      <Box key={hlIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#0F3D3E', fontWeight: 800, fontSize: '0.9rem' }}>•</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={hl}
                          placeholder="Highlight description..."
                          onChange={(e) => onEditHighlight(index, hlIdx, e.target.value)}
                          inputProps={{ maxLength: 250 }}
                          InputProps={{
                            readOnly: isViewMode,
                            sx: { bgcolor: '#ffffff', fontSize: '0.85rem' },
                          }}
                          error={Boolean(errors.highlights?.[hlIdx])}
                          helperText={errors.highlights?.[hlIdx] || undefined}
                        />
                        {!isViewMode && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveHighlight(index, hlIdx)}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block', mb: 1.5 }}>
                    No highlights added yet. Add bullet features below.
                  </Typography>
                )}

                {/* Add new highlight input */}
                {!isViewMode && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={newHighlight}
                      onChange={(e) => setNewHighlight(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={t('contacts.highlightPlaceholder') || 'Add a highlight and press Enter'}
                      inputProps={{ maxLength: 250 }}
                      InputProps={{ sx: { bgcolor: '#ffffff', fontSize: '0.85rem' } }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddHl}
                      disabled={!newHighlight.trim()}
                      sx={{
                        bgcolor: '#0F3D3E',
                        color: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        minWidth: 76,
                        minHeight: 40,
                        px: 2,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&.Mui-disabled': { bgcolor: '#dbe5e5', color: '#526768' },
                        '&:hover': { bgcolor: '#0b2e2f' },
                      }}
                    >
                      {t('contacts.addHighlight') || 'Add'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

const PricingPreviewSection = ({ title, headerDescription, items, icon }) => {
  if ((!items || items.length === 0) && !headerDescription && !title) return null;
  return (
    <Box sx={{ px: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            bgcolor: '#ecfdf5',
            color: '#0F3D3E',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: '#1e293b',
            fontWeight: 800,
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
          }}
        >
          {title}
        </Typography>
      </Box>

      {headerDescription && (
        <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
          {headerDescription}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {items.map((plan, idx) => {
          if (typeof plan === 'string') {
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ color: '#64748b', fontSize: '1rem', lineHeight: 1, mt: 0.2 }}>·</Typography>
                <Box sx={{ flex: 1 }}>
                  {renderFormattedItemText(plan)}
                </Box>
              </Box>
            );
          }
          const displayPrice = String(plan.price || '').replace(/\s*(\/\s*(month|week|year)|one[- ]?time)\s*$/i, '').trim();
          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.9rem' }}>
                  {plan.title || 'Plan'}
                </Typography>
                {plan.badge && (
                  <Chip
                    label={plan.badge}
                    size="small"
                    sx={{
                      bgcolor: '#e6fffa',
                      color: '#047481',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>

              {(plan.price || plan.period) && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F3D3E', fontSize: '1.15rem' }}>
                    {displayPrice}
                  </Typography>
                  {plan.period && (
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      {plan.period}
                    </Typography>
                  )}
                </Box>
              )}

              {plan.hint && (
                <Typography variant="caption" sx={{ display: 'block', color: '#0d9488', fontWeight: 600, fontSize: '0.75rem', mb: 0.75 }}>
                  {plan.hint}
                </Typography>
              )}

              {plan.description && (
                <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', lineHeight: 1.45, mb: 1 }}>
                  {plan.description}
                </Typography>
              )}

              {Array.isArray(plan.highlights) && plan.highlights.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {plan.highlights.map((hl, hIdx) => (
                    <Box key={hIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <CheckCircle sx={{ fontSize: 14, color: '#10b981', mt: 0.3, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: '#334155', fontSize: '0.75rem', lineHeight: 1.35 }}>
                        {hl}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

const ListSection = ({ title, items, icon }) => {
  return (
    <Box sx={{ px: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            bgcolor: '#ffebee',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: '#1e293b',
            fontWeight: 800,
            letterSpacing: '0.5px',
            fontSize: '0.75rem'
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', lineHeight: 1, mt: 0.2 }}>·</Typography>
            <Box sx={{ flex: 1 }}>
              {renderFormattedItemText(item)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LocationSection = ({ previewContact }) => {
  const mapQuery = previewContact?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(previewContact.address)}`
    : 'https://maps.google.com';

  return (
    <ContentSection title="Location">
      <Box
        sx={{
          width: '100%',
          height: '160px',
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid #e2e8f0',
          background: 'radial-gradient(circle, #e2e8f0 1.5px, transparent 1.5px) 0 0 / 16px 16px, #f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ position: 'absolute', top: '30%', left: 0, right: 0, height: '12px', bgcolor: '#ffffff', opacity: 0.8, transform: 'rotate(-5deg)' }} />
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '40%', width: '12px', bgcolor: '#ffffff', opacity: 0.8, transform: 'rotate(80deg)' }} />
        <Box sx={{ position: 'absolute', top: '70%', bottom: 0, left: '20%', width: '16px', bgcolor: '#ffffff', opacity: 0.8, transform: 'rotate(-45deg)' }} />

        <Box sx={{ position: 'absolute', top: '10%', left: '10%', width: '80px', height: '40px', bgcolor: '#e2f0d9', borderRadius: '20px', opacity: 0.7 }} />
        <Box sx={{ position: 'absolute', bottom: '15%', right: '15%', width: '90px', height: '45px', bgcolor: '#e2f0d9', borderRadius: '20px', opacity: 0.7 }} />

        <Box sx={{ zIndex: 5, animation: 'bounce 2s infinite', '@keyframes bounce': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-6px)' } } }}>
          <LocationOn sx={{ color: '#ef4444', fontSize: 40 }} />
        </Box>

        <Button
          component="a"
          href={mapQuery}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<NearMe sx={{ fontSize: 16 }} />}
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: '#ffffff',
            color: '#0f172a',
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: '10px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            zIndex: 10,
            '&:hover': { bgcolor: '#f1f5f9' }
          }}
        >
          Navigate
        </Button>
      </Box>
    </ContentSection>
  );
};

const BottomBar = ({ previewContact }) => {
  const whatsappUrl = previewContact?.whatsapp
    ? `https://wa.me/${previewContact.whatsapp.replace(/[^0-9]/g, '')}`
    : 'https://wa.me/';

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '76px',
        bgcolor: '#ffffff',
        borderTop: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1.5,
        zIndex: 100
      }}
    >
      <Button
        variant="contained"
        fullWidth
        sx={{
          bgcolor: '#073032',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '0.85rem',
          height: '46px',
          borderRadius: '10px',
          textTransform: 'none',
          letterSpacing: '0.5px',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#0b4a4e' }
        }}
      >
        SEND AN ENQUIRY
      </Button>

      <Box
        component="a"
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          width: '46px',
          height: '46px',
          borderRadius: '10px',
          bgcolor: '#25d366',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': { bgcolor: '#20ba5a' }
        }}
      >
        <WhatsApp sx={{ fontSize: 24 }} />
      </Box>
    </Box>
  );
};

export default Contacts;
